import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import backNav from "../../../assets/backNav.svg";
import Link from "../../../assets/Link.svg";
import Person from "../../../assets/Person.svg";
import AnnualHires from "../../../assets/AnnualHires.svg";
import CSV from "../../../assets/CSV.svg";
import PDFIcon from "../../../assets/PDF.svg";
import canaryLogo from "../../../assets/Logo.svg";
import Button from "../../components/CommonComponents/Button";
import CircularProgress from "@mui/material/CircularProgress";
import "./index.css";
import { useDispatch, useSelector } from "react-redux";
import {
  updateCompanies,
  updateSelectedCompnies,
} from "../../../store/actions/companyAction";
import Header from "../../components/CommonComponents/Header";
import LoadingButton from "@mui/lab/LoadingButton";
import { useMediaQuery } from "@mui/material";
import stripHtml from "../../../CommonUtils/htmlStrip";
import axiosInstance from "../../../Fbaseurl";
import {
  convertJsonToExcel,
  prepareRequiredJsonToExcelDownload,
  resolveVersionHeading,
  buildVersionedFileName,
} from "../../SalesPersonCommonMethods/taxCreditsReport";
import { showToast } from "../../../store/reducers/ToastMessageReducer";
import MapView from "../../components/CommonComponents/MapView";
import dayjs from "dayjs";
import Checkbox from "../../components/CommonComponents/Checkbox";
import Chip from "@mui/material/Chip";
import CloseIcon from "@mui/icons-material/Close";
import {
  EMPOWERMENT_FILTER_VALUES,
  EMPOWERMENT_ZONE_TYPES,
  getEmpowermentZoneCategories,
  getEmpowermentZoneCategory,
  getEmpowermentZoneLabels,
} from "../../../CommonUtils/empowermentZones";
import { openPrintPreviewFromTemplate } from "../../../CommonUtils/printPreview";
import {
  computeCreditTypeTotals,
  CREDIT_BUCKET_KEYS,
  CREDIT_TYPE_KEYS,
  resolveCreditTypeKey,
  resolveCreditTypeFromRecord,
} from "../../../CommonUtils/taxCreditTotals";
import { getRoleBasePath } from "../../../CommonUtils/roles";
import { saveAs } from "file-saver";
import NavDownloadArrow from "../../../assets/NavDownloadArrow.svg";
import { buildGraphHtml, downloadMapAsHtml, downloadMapAsPdf, downloadGraphAsPdf, renderMapCanvas, renderGraphCanvas, composeWithHeader } from "../../../CommonUtils/viewDownloads";

const CHUNK_SIZE = 30;
const TAX_CREDIT_FILTERS = {
  ALL: "all",
  FEDERAL_WOTC: CREDIT_TYPE_KEYS.FEDERAL_WOTC,
  FEDERAL_NON_WOTC: CREDIT_TYPE_KEYS.FEDERAL_NON_WOTC,
  STATE: CREDIT_TYPE_KEYS.STATE,
};
const CREDIT_TOTAL_DISPLAY = {
  ALL: "all",
  FEDERAL_WOTC: CREDIT_TYPE_KEYS.FEDERAL_WOTC,
  FEDERAL_NON_WOTC: CREDIT_TYPE_KEYS.FEDERAL_NON_WOTC,
  STATE: CREDIT_TYPE_KEYS.STATE,
};
const TAX_CREDIT_FILTER_LABELS = {
  [TAX_CREDIT_FILTERS.ALL]: "All Credits",
  [TAX_CREDIT_FILTERS.FEDERAL_WOTC]: "Federal WOTC",
  [TAX_CREDIT_FILTERS.FEDERAL_NON_WOTC]: "Federal (Non-WOTC)",
  [TAX_CREDIT_FILTERS.STATE]: "State Credits",
};
const resolveCreditFilterLabel = (value) =>
  TAX_CREDIT_FILTER_LABELS[value] || TAX_CREDIT_FILTER_LABELS[TAX_CREDIT_FILTERS.ALL];
const CREDIT_SCOPE_VALUE_SET = new Set(Object.values(CREDIT_TOTAL_DISPLAY));
const CREDIT_FILTER_VALUE_SET = new Set([
  TAX_CREDIT_FILTERS.FEDERAL_WOTC,
  TAX_CREDIT_FILTERS.FEDERAL_NON_WOTC,
  TAX_CREDIT_FILTERS.STATE,
]);
const ZONE_LABELS = {
  [EMPOWERMENT_ZONE_TYPES.EMPOWERMENT]: "Federal Empowerment Zones",
  [EMPOWERMENT_ZONE_TYPES.RURAL]: "Rural Renewal Counties",
  [EMPOWERMENT_ZONE_TYPES.NONE]: "No Zone Designation",
};
const ZONE_COLORS = {
  [EMPOWERMENT_ZONE_TYPES.EMPOWERMENT]: "#1a9c4b",
  [EMPOWERMENT_ZONE_TYPES.RURAL]: "#d97706",
  [EMPOWERMENT_ZONE_TYPES.NONE]: "#4c6ef5",
};
const NAICS_COLOR_PALETTE = [
  "#4c6ef5",
  "#0ea5e9",
  "#14b8a6",
  "#f59e0b",
  "#ec4899",
  "#6366f1",
];
const buildPieBackgroundFromSegments = (segments = []) => {
  const positive = (Array.isArray(segments) ? segments : []).filter(
    (segment) => segment.percent > 0
  );
  if (!positive.length) return "#e4e7f1";
  if (positive.length === 1) return positive[0].color;
  let current = 0;
  const stops = positive.map((segment) => {
    const start = current;
    current += segment.percent;
    return `${segment.color} ${start}% ${Math.min(current, 100)}%`;
  });
  if (current < 100) {
    stops.push(`#e4e7f1 ${current}% 100%`);
  }
  return `conic-gradient(${stops.join(", ")})`;
};

const resolveScopeFromFilter = (filterValue = TAX_CREDIT_FILTERS.ALL) => {
  if (filterValue === TAX_CREDIT_FILTERS.ALL) {
    return CREDIT_TOTAL_DISPLAY.ALL;
  }
  if (filterValue === "federal") {
    return CREDIT_TOTAL_DISPLAY.FEDERAL_NON_WOTC;
  }
  if (CREDIT_SCOPE_VALUE_SET.has(filterValue)) {
    return filterValue;
  }
  return CREDIT_TOTAL_DISPLAY.ALL;
};
const NAICS_INPUT_PATTERN = /^[0-9]{0,6}$/;
const NAICS_FILTER_PATTERN = /^[0-9]{2,6}$/;
const BUSINESS_NAICS_FIELDS = [
  "naics",
  "naicS01",
  "naicS02",
  "naicS03",
  "naicsCode",
  "naicsCodes",
];
const CREDIT_AMOUNT_FIELDS = [
  "estimatedCreditAmount",
  "estimatedAmount",
  "projectedAmount",
  "projectedTaxCreditAmount",
  "creditAmount",
  "amount",
  "value",
];
const extractTaxCreditFields = (post) => {
  if (
    typeof post.projectedCredit !== "undefined" &&
    typeof post.eligibleCredits !== "undefined"
  ) {
    return {
      projectedCredit: post.projectedCredit,
      eligibleCredits: post.eligibleCredits,
    };
  }
  let projectedCredit;
  let eligibleCredits = [];
  if (Array.isArray(post.taxCredits) && post.taxCredits.length > 0) {
    projectedCredit = post.taxCredits[0]?.totalProjectedTaxCreditAmount;
    eligibleCredits =
      post.taxCredits[0]?.availableTaxCredits?.map((c) => c.name) || [];
  }
  return {
    projectedCredit,
    eligibleCredits,
  };
};

const taxCreditCache = {};

const clearTaxCreditCache = () => {
  Object.keys(taxCreditCache).forEach((key) => {
    delete taxCreditCache[key];
  });
};

const GRAPH_CARD_MIN_WIDTH = 320;
const MAX_GRAPH_CARDS_PER_SLIDE = 4;
const TOTAL_GRAPH_CARDS = 4; // Update when graphCardBuilders array changes.

const waitForNextFrame = () => {
  if (typeof window === "undefined") return Promise.resolve();
  if (typeof window.requestAnimationFrame === "function") {
    return new Promise((resolve) => window.requestAnimationFrame(() => resolve()));
  }
  return Promise.resolve();
};

const createPrintableNode = (element) => {
  if (!element || typeof document === "undefined") return null;
  const clone = element.cloneNode(true);
  clone.id = `${element.id || "pdf-content"}-clone-${Date.now()}`;
  clone.style.position = "absolute";
  clone.style.top = "-9999px";
  clone.style.left = "-9999px";
  clone.style.width = `${element.scrollWidth || element.offsetWidth || 1024}px`;
  clone.style.maxWidth = "1200px";
  clone.style.background = "#ffffff";
  clone.style.zIndex = "0";
  clone.style.opacity = "1";
  clone.style.pointerEvents = "none";
  document.body.appendChild(clone);
  return clone;
};

const normalizeName = (value) =>
  stripHtml(value || "")
    .trim()
    .toLowerCase();

const normalizeTextValue = (value) =>
  typeof value === "string" || typeof value === "number"
    ? normalizeName(String(value))
    : "";

const resolveEntryFilterType = (label, entry = null) => {
  if (entry && typeof entry === "object") {
    const typeFromId = resolveCreditTypeFromRecord(entry);
    if (
      typeFromId &&
      typeFromId !== CREDIT_TYPE_KEYS.STATE &&
      CREDIT_FILTER_VALUE_SET.has(typeFromId)
    ) {
      return typeFromId;
    }
  }
  const resolved = resolveCreditTypeKey(label);
  if (resolved && CREDIT_FILTER_VALUE_SET.has(resolved)) {
    return resolved;
  }
  return TAX_CREDIT_FILTERS.STATE;
};

const initializeCreditTotals = () =>
  CREDIT_BUCKET_KEYS.reduce((acc, key) => {
    acc[key] = 0;
    return acc;
  }, {});

const normalizeCreditLabel = (value) => {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number") return String(value).trim();
  return "";
};

const normalizeCreditCategoryLabel = (value) => {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number") return String(value).trim();
  return "";
};

const buildCategoryKey = (value) => normalizeTextValue(value);

const parseCreditAmountValue = (value) => {
  if (value === null || typeof value === "undefined") return null;
  if (typeof value === "number") {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  if (typeof value === "string") {
    const cleaned = value.replace(/[^0-9.-]+/g, "");
    if (!cleaned) return null;
    const parsed = Number(cleaned);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

const getCreditAmountFromEntry = (entry) => {
  if (!entry || typeof entry === "string") return null;
  for (const field of CREDIT_AMOUNT_FIELDS) {
    if (
      Object.prototype.hasOwnProperty.call(entry, field) &&
      entry[field] !== null &&
      typeof entry[field] !== "undefined"
    ) {
      const parsed = parseCreditAmountValue(entry[field]);
      if (parsed !== null) {
        return parsed;
      }
    }
  }
  return null;
};

const normalizeCreditEntry = (entry) => {
  if (!entry) return null;
  if (typeof entry === "string") {
    const label = normalizeCreditLabel(entry);
    if (!label) return null;
    return {
      label,
      type: resolveEntryFilterType(label, entry),
      amount: null,
      category: null,
      categoryKey: "",
    };
  }
  if (typeof entry === "object") {
    const label =
      normalizeCreditLabel(entry.name) ||
      normalizeCreditLabel(entry.creditName) ||
      normalizeCreditLabel(entry.description);
    if (!label) return null;
    const resolvedCategory = normalizeCreditCategoryLabel(
      entry.category || entry.Category || null
    );
    return {
      label,
      type: resolveEntryFilterType(label),
      amount: getCreditAmountFromEntry(entry),
      category: resolvedCategory || null,
      categoryKey: resolvedCategory ? buildCategoryKey(resolvedCategory) : "",
    };
  }
  return null;
};

const getBusinessCreditEntries = (business = {}, taxCreditLookup = {}) => {
  let sourceEntries = [];
  const taxCreditGroups = Array.isArray(business?.taxCredits)
    ? business.taxCredits
    : [];
  if (
    taxCreditGroups.length > 0 &&
    Array.isArray(taxCreditGroups[0]?.availableTaxCredits) &&
    taxCreditGroups[0].availableTaxCredits.length > 0
  ) {
    sourceEntries = taxCreditGroups[0].availableTaxCredits;
  } else if (
    business?.prospectBusinessId &&
    taxCreditLookup[business.prospectBusinessId]
  ) {
    const cached = taxCreditLookup[business.prospectBusinessId];
    if (Array.isArray(cached?.availableCredits) && cached.availableCredits.length) {
      sourceEntries = cached.availableCredits;
    } else if (Array.isArray(cached?.eligibleCredits) && cached.eligibleCredits.length) {
      sourceEntries = cached.eligibleCredits.map((entry) =>
        typeof entry === "string" ? { name: entry } : entry
      );
    } else {
      sourceEntries = [];
    }
  } else if (Array.isArray(business?.eligibleCredits)) {
    sourceEntries = business.eligibleCredits;
  }
  return (Array.isArray(sourceEntries) ? sourceEntries : [])
    .map((entry) => normalizeCreditEntry(entry))
    .filter(Boolean);
};

const collectBusinessCategoryKeys = (
  business = {},
  taxCreditLookup = {}
) => {
  const entries = getBusinessCreditEntries(business, taxCreditLookup);
  const unique = new Map();
  entries.forEach((entry) => {
    const key = entry?.categoryKey;
    if (!key) return;
    if (!unique.has(key)) {
      unique.set(key, entry.category || "Uncategorized");
    }
  });
  if (Array.isArray(business?.creditCategories)) {
    business.creditCategories.forEach((label) => {
      const normalizedLabel = normalizeCreditCategoryLabel(label);
      if (!normalizedLabel) return;
      const key = buildCategoryKey(normalizedLabel);
      if (key && !unique.has(key)) {
        unique.set(key, normalizedLabel);
      }
    });
  }
  return Array.from(unique.entries()).map(([key, label]) => ({
    key,
    label,
  }));
};

const getBusinessProjectedCreditValue = (business = {}, taxCreditLookup = {}) => {
  if (business?.prospectBusinessId && taxCreditLookup[business.prospectBusinessId]) {
    const override = taxCreditLookup[business.prospectBusinessId];
    if (typeof override.projectedCredit !== "undefined") {
      const parsed = parseCreditAmountValue(override.projectedCredit);
      if (parsed !== null) return parsed;
    }
  }
  if (
    Array.isArray(business?.taxCredits) &&
    business.taxCredits.length > 0 &&
    typeof business.taxCredits[0]?.totalProjectedTaxCreditAmount !== "undefined"
  ) {
    const parsed = parseCreditAmountValue(
      business.taxCredits[0].totalProjectedTaxCreditAmount
    );
    if (parsed !== null) return parsed;
  }
  const fallback =
    typeof business.totalProjectedTaxCreditAmount !== "undefined"
      ? business.totalProjectedTaxCreditAmount
      : business.projectedCredit;
  const parsedFallback = parseCreditAmountValue(fallback);
  if (parsedFallback !== null) return parsedFallback;
  return null;
};

const buildCreditDisplayData = (business = {}, taxCreditLookup = {}) => {
  const entries = getBusinessCreditEntries(business, taxCreditLookup);
  const projectedCredit = getBusinessProjectedCreditValue(
    business,
    taxCreditLookup
  );
  if (!entries.length) {
    const fallbackTotal =
      typeof projectedCredit === "number" && !Number.isNaN(projectedCredit)
        ? projectedCredit
        : null;
    const hasProjectionValue =
      typeof fallbackTotal === "number" && !Number.isNaN(fallbackTotal);
    const emptyTotals = initializeCreditTotals();
    if (hasProjectionValue) {
      emptyTotals[TAX_CREDIT_FILTERS.STATE] = fallbackTotal;
    }
    return {
      entries: [],
      totals: {
        federalWotc: emptyTotals[TAX_CREDIT_FILTERS.FEDERAL_WOTC],
        federalNonWotc: emptyTotals[TAX_CREDIT_FILTERS.FEDERAL_NON_WOTC],
        state: emptyTotals[TAX_CREDIT_FILTERS.STATE],
        total: hasProjectionValue ? fallbackTotal : 0,
      },
      projectedCredit: hasProjectionValue ? fallbackTotal : null,
      hasProjectionValue,
    };
  }
  let definedSum = 0;
  const enhancedEntries = entries.map((entry) => {
    const baseAmount =
      typeof entry.amount === "number" && entry.amount > 0 ? entry.amount : 0;
    if (baseAmount > 0) {
      definedSum += baseAmount;
    }
    return {
      label: entry.label,
      type: entry.type,
      amount: baseAmount,
      category: entry.category || null,
      categoryKey: entry.categoryKey || "",
    };
  });
  const normalizedProjected =
    typeof projectedCredit === "number" && !Number.isNaN(projectedCredit)
      ? projectedCredit
      : null;
  if (normalizedProjected !== null && enhancedEntries.length > 0) {
    const remainder = normalizedProjected - definedSum;
    if (remainder > 0) {
      const share = remainder / enhancedEntries.length;
      enhancedEntries.forEach((entry) => {
        entry.amount += share;
      });
    }
  }
  const totals = initializeCreditTotals();
  enhancedEntries.forEach((entry) => {
    if (typeof entry.amount === "number" && entry.amount > 0) {
      totals[entry.type] += entry.amount;
    }
  });
  const totalValue =
    normalizedProjected !== null
      ? normalizedProjected
      : CREDIT_BUCKET_KEYS.reduce((sum, key) => sum + totals[key], 0);
  return {
    entries: enhancedEntries,
    totals: {
      federalWotc: totals[TAX_CREDIT_FILTERS.FEDERAL_WOTC],
      federalNonWotc: totals[TAX_CREDIT_FILTERS.FEDERAL_NON_WOTC],
      state: totals[TAX_CREDIT_FILTERS.STATE],
      total: totalValue,
    },
    projectedCredit: totalValue,
    hasProjectionValue:
      normalizedProjected !== null ||
      enhancedEntries.some((entry) => entry.amount > 0),
  };
};

const sortBusinessesByRelevance = (items, searchBusinessName) => {
  const searchName = normalizeName(searchBusinessName || "");
  return (Array.isArray(items) ? items : [])
    .map((item, index) => {
      const normalizedName = normalizeName(item.businessName || "");
      const isExact = !!searchName && normalizedName === searchName;
      const isPrefix =
        !!searchName && !isExact && normalizedName.startsWith(searchName);
      const isContains =
        !!searchName && !isExact && !isPrefix && normalizedName.includes(searchName);

      let score = 0;
      if (isExact) score = 3;
      else if (isPrefix) score = 2;
      else if (isContains) score = 1;

      return { item, score, index };
    })
    .sort((a, b) => {
      const hasSearchName = !!searchName;
      const aExact =
        hasSearchName &&
        normalizeName(a.item.businessName || "") === searchName;
      const bExact =
        hasSearchName &&
        normalizeName(b.item.businessName || "") === searchName;

      if (aExact && !bExact) return -1;
      if (!aExact && bExact) return 1;

      if (b.score !== a.score) return b.score - a.score;
      return a.index - b.index;
    })
    .map((entry) => entry.item);
};

const getBusinessSelectionKey = (business) => {
  if (!business) return "";
  return (
    business.prospectBusinessId ||
    business.businessId ||
    [
      stripHtml(business.businessName || ""),
      business.physicalAddress || business.streetAddress || "",
      business.physicalCity || business.city || "",
      business.state || "",
      business.physicalZip || business.zip || "",
    ].join("|")
  );
};

const dedupeBusinesses = (list = []) => {
  const seen = new Map();
  const withoutKeys = [];
  (Array.isArray(list) ? list : []).forEach((biz) => {
    const key = getBusinessSelectionKey(biz);
    if (!key) {
      withoutKeys.push(biz);
      return;
    }
    if (!seen.has(key)) {
      seen.set(key, biz);
    }
  });
  return [...seen.values(), ...withoutKeys];
};

const formatBusinessAddress = (biz) =>
  [biz.physicalAddress, biz.physicalCity, biz.state, biz.physicalZip]
    .filter(Boolean)
    .join(", ");

const getTaxCreditDescriptions = (taxCreditsArr) => {
  if (!Array.isArray(taxCreditsArr) || taxCreditsArr.length === 0) return [];
  return (taxCreditsArr[0].availableTaxCredits || []).map((c) => {
    const baseName = c?.name || "";
    const category = c?.category || c?.Category;
    const label = category ? `${baseName} (${category})` : baseName;
    return [label || "Unnamed credit", c?.description]
      .filter(Boolean)
      .join(": ");
  });
};

const getBusinessEligibleCreditNames = (
  business = {},
  taxCreditLookup = {}
) => {
  return getBusinessCreditEntries(business, taxCreditLookup).map(
    (entry) => entry.label
  );
};

const buildEmployeeRangeHeaders = (params = {}) => {
  const headers = {};
  if (params.employeeRangeStart) headers.employeeRangeMin = params.employeeRangeStart;
  if (params.employeeRangeEnd) headers.employeeRangeMax = params.employeeRangeEnd;
  return headers;
};

const formatEmployeesDisplay = (business = {}) => {
  const rawValue = business?.locationEmployeesCount;
  const numericValue =
    typeof rawValue === "number" ? rawValue : Number(rawValue);

  if (typeof numericValue === "number" && !Number.isNaN(numericValue)) {
    if (numericValue > 0) {
      return numericValue.toLocaleString();
    }
    if (numericValue === 0) {
      const annualHiresRaw =
        business?.annualHires ??
        business?.AnnualHires ??
        business?.annualHiresOverride;
      const annualHires = Number(annualHiresRaw);
      if (!Number.isNaN(annualHires) && annualHires > 0) {
        const estimatedEmployees = Math.ceil(annualHires * 5);
        return estimatedEmployees.toLocaleString();
      }
    }
  }

  return "No Data";
};

const buildSearchRequestConfig = (params = {}) => {
  const serializer = (values = {}) => {
    const search = new URLSearchParams();
    Object.entries(values).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((entry) => {
          if (entry !== undefined && entry !== null && entry !== "") {
            search.append(key, entry);
          }
        });
      } else if (
        value !== undefined &&
        value !== null &&
        value !== ""
      ) {
        search.append(key, value);
      }
    });
    return search.toString();
  };
  return {
    params,
    paramsSerializer: serializer,
  };
};

const hasSearchFilters = (filters = {}) => {
  if (!filters) return false;
  const simpleKeys = [
    "businessName",
    "streetAddress",
    "city",
    "state",
    "zip",
    "industry",
    "minEmployeeCount",
    "maxEmployeeCount",
    "employeeMin",
    "employeeMax",
  ];
  if (
    simpleKeys.some((key) => {
      const value = filters[key];
      return value !== undefined && value !== null && String(value).trim() !== "";
    })
  ) {
    return true;
  }
  if (Array.isArray(filters.naicsCodes) && filters.naicsCodes.length) return true;
  if (Array.isArray(filters.censusCodes) && filters.censusCodes.length) return true;
  return false;
};

const normalizeCodeValue = (value) => {
  if (value === undefined || value === null) return "";
  return String(value).trim().toLowerCase();
};

const collectBusinessCodes = (business = {}, fieldNames = []) => {
  const codes = [];
  const pushValue = (value) => {
    if (value === undefined || value === null) return;
    const strValue = String(value);
    strValue
      .split(/[,;|]/)
      .map((segment) => segment.trim())
      .filter(Boolean)
      .forEach((segment) => {
        const normalized = normalizeCodeValue(segment);
        if (normalized) codes.push(normalized);
      });
  };
  fieldNames.forEach((field) => {
    const data = business?.[field];
    if (Array.isArray(data)) {
      data.forEach(pushValue);
    } else {
      pushValue(data);
    }
  });
  return codes;
};

const sanitizeNaicsDigits = (value) => {
  if (value === undefined || value === null) return "";
  return String(value).replace(/[^0-9]/g, "");
};

const getPrimaryNaicsCode = (business = {}) => {
  const codes = collectBusinessCodes(business, BUSINESS_NAICS_FIELDS);
  for (const code of codes) {
    const digits = sanitizeNaicsDigits(code);
    if (digits.length >= 2) return digits.slice(0, 6);
  }
  return "";
};

const doesBusinessMatchCodes = (
  business,
  filterValues = [],
  businessFields = [],
  options = {}
) => {
  const normalizedFilters = (Array.isArray(filterValues) ? filterValues : [])
    .map((value) => normalizeCodeValue(value))
    .filter(Boolean);
  if (!normalizedFilters.length) return false;
  const businessCodes = collectBusinessCodes(business, businessFields);
  if (!businessCodes.length) return false;
  if (options.allowPrefixMatch) {
    return businessCodes.some((code) =>
      normalizedFilters.some((filter) => code.startsWith(filter))
    );
  }
  const filterSet = new Set(normalizedFilters);
  return businessCodes.some((code) => filterSet.has(code));
};

const businessMatchesCodeFilters = (business = {}, filters = {}) => {
  const hasNaicsFilters = Array.isArray(filters.naicsCodes) && filters.naicsCodes.length > 0;
  const hasCensusFilters = Array.isArray(filters.censusCodes) && filters.censusCodes.length > 0;
  if (!hasNaicsFilters && !hasCensusFilters) return false;
  const matchesNaics =
    !hasNaicsFilters ||
    doesBusinessMatchCodes(
      business,
      filters.naicsCodes,
      [
        "naics",
        "naicS01",
        "naicS02",
        "naicS03",
        "naicsCode",
        "naicsCodes",
      ],
      { allowPrefixMatch: true }
    );
  const matchesCensus =
    !hasCensusFilters ||
    doesBusinessMatchCodes(business, filters.censusCodes, [
      "censusTract",
      "censusTractCode",
      "CensusTract",
      "censusCodes",
      "censusCode",
    ]);
  return matchesNaics && matchesCensus;
};

const hasFilterValue = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  return value !== undefined && value !== null && String(value).trim() !== "";
};

const parseNumericFilterValue = (value) => {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isNaN(parsed) ? null : parsed;
};

const getBusinessEmployeeCount = (business = {}) => {
  const raw =
    business?.locationEmployeesCount ??
    business?.employees ??
    business?.Employees ??
    business?.employeesTotal;
  const numeric =
    typeof raw === "number" ? raw : Number(raw);
  if (typeof numeric === "number" && !Number.isNaN(numeric)) {
    return numeric;
  }
  return null;
};

const businessMatchesIndustryFilter = (business = {}, value) => {
  const normalizedTarget = normalizeTextValue(value);
  if (!normalizedTarget) return false;
  const candidates = [
    business?.industry,
    business?.siC01Description,
    business?.naicsDescription,
    business?.naicS01Description,
    business?.primarySIC4DigitDescription,
    business?.primarySIC2DigitDescription,
  ]
    .map((entry) => normalizeTextValue(entry))
    .filter(Boolean);
  if (!candidates.length) return false;
  return candidates.some((entry) => entry === normalizedTarget);
};

const businessMatchesTextFilter = (business = {}, filterValue, fieldCandidates = []) => {
  const normalizedTarget = normalizeTextValue(filterValue);
  if (!normalizedTarget) return false;
  return fieldCandidates.some((field) => {
    const candidate = normalizeTextValue(business?.[field]);
    return candidate && candidate === normalizedTarget;
  });
};

const filtersIncludeExactCriteria = (filters = {}) => {
  if (!filters) return false;
  return (
    hasFilterValue(filters.businessName) ||
    hasFilterValue(filters.streetAddress) ||
    hasFilterValue(filters.city) ||
    hasFilterValue(filters.state) ||
    hasFilterValue(filters.zip) ||
    hasFilterValue(filters.industry) ||
    hasFilterValue(filters.minEmployeeCount) ||
    hasFilterValue(filters.maxEmployeeCount) ||
    hasFilterValue(filters.employeeMin) ||
    hasFilterValue(filters.employeeMax) ||
    (Array.isArray(filters.naicsCodes) && filters.naicsCodes.length > 0) ||
    (Array.isArray(filters.censusCodes) && filters.censusCodes.length > 0)
  );
};

const businessMatchesAllFilters = (business = {}, filters = {}) => {
  if (!filters) return false;
  let hasCriteria = false;
  const textConfigs = [
    { key: "businessName", fields: ["businessName"] },
    { key: "streetAddress", fields: ["physicalAddress", "streetAddress"] },
    { key: "city", fields: ["physicalCity", "city"] },
    { key: "state", fields: ["state"] },
    { key: "zip", fields: ["physicalZip", "zip"] },
  ];
  for (const config of textConfigs) {
    if (hasFilterValue(filters[config.key])) {
      hasCriteria = true;
      if (!businessMatchesTextFilter(business, filters[config.key], config.fields)) {
        return false;
      }
    }
  }
  if (hasFilterValue(filters.industry)) {
    hasCriteria = true;
    if (!businessMatchesIndustryFilter(business, filters.industry)) {
      return false;
    }
  }
  const hasCodeFilters =
    (Array.isArray(filters.naicsCodes) && filters.naicsCodes.length > 0) ||
    (Array.isArray(filters.censusCodes) && filters.censusCodes.length > 0);
  if (hasCodeFilters) {
    hasCriteria = true;
    if (!businessMatchesCodeFilters(business, filters)) {
      return false;
    }
  }
  const employeeMinFilter = parseNumericFilterValue(
    filters.employeeMin ?? filters.minEmployeeCount
  );
  const employeeMaxFilter = parseNumericFilterValue(
    filters.employeeMax ?? filters.maxEmployeeCount
  );
  if (employeeMinFilter !== null || employeeMaxFilter !== null) {
    hasCriteria = true;
    const employeeCount = getBusinessEmployeeCount(business);
    if (employeeCount === null) return false;
    if (
      (employeeMinFilter !== null && employeeCount < employeeMinFilter) ||
      (employeeMaxFilter !== null && employeeCount > employeeMaxFilter)
    ) {
      return false;
    }
  }
  return hasCriteria;
};

const Blog = () => {
  const isMobile = useMediaQuery("(max-width:600px)");
  const dispatch = useDispatch();
  const companies = useSelector((state) => state.company.companies);
  const currentRole = useSelector((state) => state.App.currentRole);
  const roleBasePath = getRoleBasePath(currentRole);
  const appUserName =
    useSelector((state) => state.App.userName) || "Tax Credit Specialist";
  const navigate = useNavigate();
  const location = useLocation();
  const {
    filters = {},
    isProximityBasedCount,
    isEaxctNameSearchSelected,
    recordCount: passedRecordCount,
    searchMapCenter,
    isManualSearch = false,
    annualHires: manualAnnualHires, // custom from manual search flow
    initialZoneFilter: initialZoneFilterFromNav,
    enforcedZoneFilters: enforcedZoneFiltersFromNav,
  } = location.state || {};

  const [totalRecords, setTotalRecords] = useState(null);
  const [displayedResultsCount, setDisplayedResultsCount] = useState(0);
  const [fetchedResultCount, setFetchedResultCount] = useState(0);
  const [isLoading, setLoader] = useState(false);
  const [error, setError] = useState(null);
  const [taxCreditData, setTaxCreditData] = useState({});
  const [taxCreditLoading, setTaxCreditLoading] = useState({});
  const [allBusinesses, setAllBusinesses] = useState([]);
  const [annualHiresOverrides, setAnnualHiresOverrides] = useState({});
  const [annualHiresDrafts, setAnnualHiresDrafts] = useState({});
  const [annualHiresSaving, setAnnualHiresSaving] = useState({});
  const [isSearchComplete, setIsSearchComplete] = useState(false);
  const [nextChunkStart, setNextChunkStart] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [selectedBusinesses, setSelectedBusinesses] = useState([]);
  const [isSelectionTrayCollapsed, setSelectionTrayCollapsed] = useState(false);
  const allowedZoneFilterValues = [
    ...Object.values(EMPOWERMENT_FILTER_VALUES),
    EMPOWERMENT_ZONE_TYPES.NONE,
  ];
  const initialZoneFilterValue = allowedZoneFilterValues.includes(initialZoneFilterFromNav)
    ? initialZoneFilterFromNav
    : EMPOWERMENT_FILTER_VALUES.ALL;
  const [empowermentFilter, setEmpowermentFilter] = useState(initialZoneFilterValue);
  const [naicsFilter, setNaicsFilter] = useState("");
  const [taxCreditFilter, setTaxCreditFilter] = useState(TAX_CREDIT_FILTERS.ALL);
  const [creditCategoryFilter, setCreditCategoryFilter] = useState([]);
  const [isFilterMenuOpen, setFilterMenuOpen] = useState(false);
  const filterMenuRef = useRef(null);
  const [viewMode, setViewMode] = useState("map");
  const naicsDirectoryRef = useRef({});
  const naicsPrefixDirectoryRef = useRef({});
  const naicsDirectoryPromiseRef = useRef(null);
  const hasLoadedNaicsDirectoryRef = useRef(false);
  const [naicsDirectoryVersion, setNaicsDirectoryVersion] = useState(0);
  const creditTotalsScope = resolveScopeFromFilter(taxCreditFilter);
  const activeCreditFilter = taxCreditFilter;
  const enforcedZoneFilters = useMemo(() => {
    if (!Array.isArray(enforcedZoneFiltersFromNav)) return [];
    return enforcedZoneFiltersFromNav.filter((value) =>
      value === EMPOWERMENT_FILTER_VALUES.EMPOWERMENT ||
      value === EMPOWERMENT_FILTER_VALUES.RURAL
    );
  }, [enforcedZoneFiltersFromNav]);
  const hasEnforcedZones = enforcedZoneFilters.length > 0;
  const ensureNaicsDirectory = useCallback(async () => {
    if (hasLoadedNaicsDirectoryRef.current) {
      return {
        codes: naicsDirectoryRef.current,
        prefixes: naicsPrefixDirectoryRef.current,
      };
    }
    if (!naicsDirectoryPromiseRef.current) {
      naicsDirectoryPromiseRef.current = axiosInstance
        .get("/businessSearch/GetNaicsCodes")
        .then((response) => {
          const rawData = Array.isArray(response?.data)
            ? response.data
            : Array.isArray(response?.data?.data)
            ? response.data.data
            : [];
          const codeDirectory = {};
          const prefixDirectory = {};
          rawData.forEach((item) => {
            const normalized = sanitizeNaicsDigits(item?.naicsCode);
            if (!normalized) return;
            codeDirectory[normalized] = item?.naicsDescription || "";
            const prefix = normalized.slice(0, 2);
            if (prefix && !prefixDirectory[prefix]) {
              prefixDirectory[prefix] = item?.naicsDescription || "";
            }
          });
          naicsDirectoryRef.current = codeDirectory;
          naicsPrefixDirectoryRef.current = prefixDirectory;
          hasLoadedNaicsDirectoryRef.current = true;
          setNaicsDirectoryVersion((prev) => prev + 1);
          return { codes: codeDirectory, prefixes: prefixDirectory };
        })
        .catch((error) => {
          console.error("Unable to load NAICS codes", error);
          return {
            codes: naicsDirectoryRef.current,
            prefixes: naicsPrefixDirectoryRef.current,
          };
        })
        .finally(() => {
          naicsDirectoryPromiseRef.current = null;
        });
    }
    return naicsDirectoryPromiseRef.current;
  }, []);

  useEffect(() => {
    ensureNaicsDirectory();
  }, [ensureNaicsDirectory]);
  const availableCreditCategories = useMemo(() => {
    if (!Array.isArray(allBusinesses) || !allBusinesses.length) return [];
    const counts = new Map();
    allBusinesses.forEach((biz) => {
      const categories = collectBusinessCategoryKeys(biz, taxCreditData);
      categories.forEach(({ key, label }) => {
        if (!key) return;
        const current = counts.get(key);
        if (current) {
          current.count += 1;
        } else {
          counts.set(key, {
            key,
            label: label || "Uncategorized",
            count: 1,
          });
        }
      });
    });
    return Array.from(counts.values()).sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return a.label.localeCompare(b.label);
    });
  }, [allBusinesses, taxCreditData]);

  useEffect(() => {
    setEmpowermentFilter(initialZoneFilterValue);
  }, [initialZoneFilterValue]);
  useEffect(() => {
    if (!isFilterMenuOpen) return undefined;
    if (typeof document === "undefined") return undefined;
    const handleClickOutside = (event) => {
      if (
        filterMenuRef.current &&
        !filterMenuRef.current.contains(event.target)
      ) {
        setFilterMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isFilterMenuOpen]);
  const [isPdfDownloading, setIsPdfDownloading] = useState(false);
  const baseParamsRef = useRef(null);
  const shouldShowNoRecordsMessage =
    isSearchComplete && !isLoading && allBusinesses.length === 0;
  const sanitizedNaicsFilter = naicsFilter.trim();
  const isNaicsFilterActive = NAICS_FILTER_PATTERN.test(sanitizedNaicsFilter);
  const isNaicsFilterPending =
    naicsFilter !== "" && !isNaicsFilterActive;
  const handleNaicsFilterChange = useCallback((value) => {
    if (value === "" || NAICS_INPUT_PATTERN.test(value)) {
      setNaicsFilter(value);
    }
  }, []);
  const handleCategoryFilterToggle = useCallback((categoryKey) => {
    if (!categoryKey) return;
    setCreditCategoryFilter((prev) => {
      const next = Array.isArray(prev) ? [...prev] : [];
      if (next.includes(categoryKey)) {
        return next.filter((value) => value !== categoryKey);
      }
      next.push(categoryKey);
      return next;
    });
  }, []);
  const handleResetFilters = useCallback(() => {
    setEmpowermentFilter(EMPOWERMENT_FILTER_VALUES.ALL);
    setNaicsFilter("");
    setTaxCreditFilter(TAX_CREDIT_FILTERS.ALL);
    setCreditCategoryFilter([]);
  }, []);
  const handleViewModeChange = useCallback((mode) => {
    setViewMode(mode);
  }, []);
  const [graphSlideIndex, setGraphSlideIndex] = useState(0);
  const [cardsPerSlide, setCardsPerSlide] = useState(2);
  const graphViewRef = useRef(null);
  const measureGraphCardsPerSlide = useCallback(() => {
    if (typeof window === "undefined") return;
    const containerWidth =
      graphViewRef.current?.clientWidth ||
      graphViewRef.current?.offsetWidth ||
      window.innerWidth;
    if (!containerWidth) return;
    const nextValue = Math.max(
      1,
      Math.min(
        MAX_GRAPH_CARDS_PER_SLIDE,
        Math.floor(containerWidth / GRAPH_CARD_MIN_WIDTH) || 1
      )
    );
    setCardsPerSlide((prev) => (prev === nextValue ? prev : nextValue));
  }, []);
  useEffect(() => {
    if (viewMode !== "graph") return undefined;
    measureGraphCardsPerSlide();
    if (typeof window === "undefined") return undefined;
    const handleResize = () => measureGraphCardsPerSlide();
    window.addEventListener("resize", handleResize);
    let observer;
    if (typeof ResizeObserver !== "undefined" && graphViewRef.current) {
      observer = new ResizeObserver(handleResize);
      observer.observe(graphViewRef.current);
    }
    return () => {
      window.removeEventListener("resize", handleResize);
      if (observer) {
        observer.disconnect();
      }
    };
  }, [measureGraphCardsPerSlide, viewMode]);
  useEffect(() => {
    setGraphSlideIndex((prev) => {
      const slidesPerView = Math.max(cardsPerSlide, 1);
      const computedMaxIndex = Math.max(
        Math.ceil(TOTAL_GRAPH_CARDS / slidesPerView) - 1,
        0
      );
      return prev > computedMaxIndex ? computedMaxIndex : prev;
    });
  }, [cardsPerSlide]);
  useEffect(() => {
    if (viewMode !== "graph") {
      setGraphSlideIndex(0);
    }
  }, [viewMode]);
  const hasCategoryFilters =
    Array.isArray(creditCategoryFilter) && creditCategoryFilter.length > 0;
  const categoryFilterSet = useMemo(() => {
    if (!hasCategoryFilters) return null;
    return new Set(
      creditCategoryFilter
        .filter(Boolean)
        .map((value) => buildCategoryKey(value))
        .filter(Boolean)
    );
  }, [creditCategoryFilter, hasCategoryFilters]);
  const visibleBusinesses = useMemo(() => {
    let list = Array.isArray(allBusinesses) ? allBusinesses : [];
    const creditDisplayCache = new Map();
    const resolveCreditDisplay = (biz) => {
      if (!biz) return null;
      const cacheKey =
        getBusinessSelectionKey(biz) ||
        biz?.prospectBusinessId ||
        biz?.businessId;
      if (cacheKey && creditDisplayCache.has(cacheKey)) {
        return creditDisplayCache.get(cacheKey);
      }
      const data = buildCreditDisplayData(biz, taxCreditData);
      if (cacheKey) {
        creditDisplayCache.set(cacheKey, data);
      }
      return data;
    };
    const shouldFilterCategories =
      categoryFilterSet && categoryFilterSet.size > 0;
    if (hasEnforcedZones) {
      list = list.filter((biz) =>
        enforcedZoneFilters.some((value) =>
          getEmpowermentZoneCategories(biz.empowermentZone).includes(value)
        )
      );
    }
    if (empowermentFilter === EMPOWERMENT_ZONE_TYPES.NONE) {
      list = list.filter(
        (biz) => getEmpowermentZoneCategories(biz.empowermentZone).length === 0
      );
    } else if (empowermentFilter !== EMPOWERMENT_FILTER_VALUES.ALL) {
      list = list.filter((biz) =>
        getEmpowermentZoneCategories(biz.empowermentZone).includes(
          empowermentFilter
        )
      );
    }
    if (isNaicsFilterActive) {
      list = list.filter((biz) =>
        doesBusinessMatchCodes(
          biz,
          [sanitizedNaicsFilter],
          BUSINESS_NAICS_FIELDS,
          { allowPrefixMatch: true }
        )
      );
    }
    if (shouldFilterCategories) {
      list = list.filter((biz) => {
        const creditData = resolveCreditDisplay(biz);
        if (!creditData) return false;
        const categories = new Set(
          creditData.entries
            .map((entry) => entry.categoryKey)
            .filter(Boolean)
        );
        if (Array.isArray(biz.creditCategories)) {
          biz.creditCategories.forEach((label) => {
            const key = buildCategoryKey(label);
            if (key) {
              categories.add(key);
            }
          });
        }
        if (!categories.size) return false;
        for (const key of categoryFilterSet.values()) {
          if (categories.has(key)) {
            return true;
          }
        }
        return false;
      });
    }
    if (activeCreditFilter !== TAX_CREDIT_FILTERS.ALL) {
      list = list.filter((biz) => {
        const creditData = resolveCreditDisplay(biz);
        if (!creditData) return false;
        if (activeCreditFilter === TAX_CREDIT_FILTERS.STATE) {
          const hasStateEntries = creditData.entries.some(
            (entry) => entry.type === TAX_CREDIT_FILTERS.STATE
          );
          const qualifiesFromProjection =
            creditData.entries.length === 0 && creditData.hasProjectionValue;
          return hasStateEntries || qualifiesFromProjection;
        }
        return creditData.entries.some(
          (entry) => entry.type === activeCreditFilter
        );
      });
    }
    return list;
  }, [
    allBusinesses,
    empowermentFilter,
    enforcedZoneFilters,
    hasEnforcedZones,
    isNaicsFilterActive,
    sanitizedNaicsFilter,
    taxCreditData,
    activeCreditFilter,
    categoryFilterSet,
  ]);
  const mapBusinessesToShow = visibleBusinesses;
  const baseCreditTotals = useMemo(
    () => computeCreditTypeTotals(mapBusinessesToShow, taxCreditData),
    [mapBusinessesToShow, taxCreditData]
  );
  const creditTotals = useMemo(() => {
    if (!categoryFilterSet || !categoryFilterSet.size) {
      return baseCreditTotals;
    }
    const totals = {
      federalWotc: 0,
      federalNonWotc: 0,
      state: 0,
      total: 0,
    };
    mapBusinessesToShow.forEach((biz) => {
      const creditData = buildCreditDisplayData(biz, taxCreditData);
      creditData.entries.forEach((entry) => {
        if (!entry.categoryKey || !categoryFilterSet.has(entry.categoryKey)) {
          return;
        }
        if (typeof entry.amount !== "number" || entry.amount <= 0) {
          return;
        }
        const bucket =
          entry.type === TAX_CREDIT_FILTERS.FEDERAL_WOTC
            ? "federalWotc"
            : entry.type === TAX_CREDIT_FILTERS.FEDERAL_NON_WOTC
            ? "federalNonWotc"
            : "state";
        totals[bucket] += entry.amount;
      });
    });
    totals.total = totals.federalWotc + totals.federalNonWotc + totals.state;
    return totals;
  }, [
    baseCreditTotals,
    categoryFilterSet,
    mapBusinessesToShow,
    taxCreditData,
  ]);
  const graphSummary = useMemo(() => {
    const summary = {
      totalBusinesses: visibleBusinesses.length,
      counts: {
        [TAX_CREDIT_FILTERS.FEDERAL_WOTC]: 0,
        [TAX_CREDIT_FILTERS.FEDERAL_NON_WOTC]: 0,
        [TAX_CREDIT_FILTERS.STATE]: 0,
      },
      amounts: {
        federalWotc: creditTotals.federalWotc,
        federalNonWotc: creditTotals.federalNonWotc,
        state: creditTotals.state,
        total: creditTotals.total,
      },
    };
    visibleBusinesses.forEach((biz) => {
      const creditData = buildCreditDisplayData(biz, taxCreditData);
      const hasFederalWotc = creditData.entries.some(
        (entry) => entry.type === TAX_CREDIT_FILTERS.FEDERAL_WOTC
      );
      const hasFederalNonWotc = creditData.entries.some(
        (entry) => entry.type === TAX_CREDIT_FILTERS.FEDERAL_NON_WOTC
      );
      const hasState =
        creditData.entries.some(
          (entry) => entry.type === TAX_CREDIT_FILTERS.STATE
        ) ||
        (!creditData.entries.length && creditData.hasProjectionValue);
      if (hasFederalWotc) {
        summary.counts[TAX_CREDIT_FILTERS.FEDERAL_WOTC] += 1;
      }
      if (hasFederalNonWotc) {
        summary.counts[TAX_CREDIT_FILTERS.FEDERAL_NON_WOTC] += 1;
      }
      if (hasState) {
        summary.counts[TAX_CREDIT_FILTERS.STATE] += 1;
      }
    });
    return summary;
  }, [visibleBusinesses, taxCreditData, creditTotals]);
  const zoneDistribution = useMemo(() => {
    const counts = {
      [EMPOWERMENT_ZONE_TYPES.EMPOWERMENT]: 0,
      [EMPOWERMENT_ZONE_TYPES.RURAL]: 0,
      [EMPOWERMENT_ZONE_TYPES.NONE]: 0,
    };
    visibleBusinesses.forEach((biz) => {
      const category = getEmpowermentZoneCategory(biz.empowermentZone);
      counts[category] = (counts[category] || 0) + 1;
    });
    const total = visibleBusinesses.length;
    const items = Object.keys(counts).map((key) => ({
      key,
      label: ZONE_LABELS[key],
      color: ZONE_COLORS[key],
      count: counts[key],
      percent: total > 0 ? Math.round((counts[key] / total) * 100) : 0,
    }));
    return { total, items };
  }, [visibleBusinesses]);
  const naicsDistribution = useMemo(() => {
    if (!visibleBusinesses.length) return { total: 0, items: [] };
    const bucketCounts = {};
    const bucketDescriptions = {};
    let unknownCount = 0;
    const prefixDirectory = naicsPrefixDirectoryRef.current || {};
    visibleBusinesses.forEach((biz) => {
      const code = getPrimaryNaicsCode(biz);
      if (code && code.length >= 2) {
        const prefix = code.slice(0, 2);
        bucketCounts[prefix] = (bucketCounts[prefix] || 0) + 1;
        if (!bucketDescriptions[prefix]) {
          const directoryDescription = prefixDirectory[prefix];
          const businessDescription =
            biz?.naicsDescription ||
            biz?.naicS01Description ||
            biz?.naicsDescription ||
            "";
          bucketDescriptions[prefix] =
            directoryDescription || businessDescription || "";
        }
      } else {
        unknownCount += 1;
      }
    });
    const sortedEntries = Object.keys(bucketCounts)
      .map((prefix) => ({
        key: prefix,
        label: bucketDescriptions[prefix]
          ? `${bucketDescriptions[prefix]} (${prefix})`
          : `NAICS ${prefix}`,
        count: bucketCounts[prefix],
      }))
      .sort((a, b) => b.count - a.count);
    const MAX_SEGMENTS = 5;
    let displayed = sortedEntries.slice(0, MAX_SEGMENTS);
    const remaining = sortedEntries.slice(MAX_SEGMENTS);
    const otherCount = remaining.reduce((sum, entry) => sum + entry.count, 0);
    const items = [...displayed];
    if (otherCount > 0) {
      items.push({ key: "other", label: "Other NAICS", count: otherCount });
    }
    if (unknownCount > 0) {
      items.push({ key: "unknown", label: "Unknown", count: unknownCount });
    }
    const total = visibleBusinesses.length;
    const withMeta = items.map((item, index) => ({
      ...item,
      color: NAICS_COLOR_PALETTE[index % NAICS_COLOR_PALETTE.length],
      percent: total > 0 ? Math.round((item.count / total) * 100) : 0,
    }));
    return { total, items: withMeta };
  }, [visibleBusinesses, naicsDirectoryVersion]);
  const categoryDistribution = useMemo(() => {
    if (!visibleBusinesses.length) {
      return { totalAmount: 0, totalCount: 0, items: [] };
    }
    const aggregates = new Map();
    visibleBusinesses.forEach((biz) => {
      const creditData = buildCreditDisplayData(biz, taxCreditData);
      const amountByCategory = new Map();
      creditData.entries.forEach((entry) => {
        const key = entry.categoryKey;
        if (!key) return;
        const amount =
          typeof entry.amount === "number" && !Number.isNaN(entry.amount)
            ? entry.amount
            : 0;
        amountByCategory.set(
          key,
          (amountByCategory.get(key) || 0) + amount
        );
      });
      const categories = collectBusinessCategoryKeys(biz, taxCreditData);
      categories.forEach(({ key, label }) => {
        if (!key) return;
        if (categoryFilterSet && categoryFilterSet.size) {
          if (!categoryFilterSet.has(key)) return;
        }
        const aggregate = aggregates.get(key) || {
          key,
          label,
          amount: 0,
          count: 0,
        };
        aggregate.label = label;
        aggregate.count += 1;
        const contribution = amountByCategory.get(key);
        if (typeof contribution === "number") {
          aggregate.amount += contribution;
        }
        aggregates.set(key, aggregate);
      });
    });
    const entries = Array.from(aggregates.values()).filter(
      (item) => item.amount > 0 || item.count > 0
    );
    if (!entries.length) {
      return { totalAmount: 0, totalCount: 0, items: [] };
    }
    entries.sort((a, b) => {
      if (b.amount !== a.amount) return b.amount - a.amount;
      if (b.count !== a.count) return b.count - a.count;
      return a.label.localeCompare(b.label);
    });
    const MAX_SEGMENTS = 5;
    const displayed = entries.slice(0, MAX_SEGMENTS);
    const remainder = entries.slice(MAX_SEGMENTS);
    if (remainder.length) {
      displayed.push({
        key: "other",
        label: "Other Categories",
        amount: remainder.reduce((sum, entry) => sum + entry.amount, 0),
        count: remainder.reduce((sum, entry) => sum + entry.count, 0),
      });
    }
    const totalAmount = displayed.reduce(
      (sum, entry) => sum + (entry.amount || 0),
      0
    );
    const totalCount = displayed.reduce(
      (sum, entry) => sum + (entry.count || 0),
      0
    );
    const hasAmount = totalAmount > 0;
    const basisTotal = hasAmount ? totalAmount : totalCount;
    const items = displayed.map((entry, index) => {
      const basis = hasAmount ? entry.amount : entry.count;
      const percent =
        basisTotal > 0 ? Math.round(((basis || 0) / basisTotal) * 100) : 0;
      return {
        ...entry,
        color: NAICS_COLOR_PALETTE[index % NAICS_COLOR_PALETTE.length],
        percent,
      };
    });
    return { totalAmount, totalCount, items };
  }, [visibleBusinesses, taxCreditData, categoryFilterSet]);
  const showFederalWotcChip =
    creditTotalsScope === CREDIT_TOTAL_DISPLAY.ALL ||
    creditTotalsScope === CREDIT_TOTAL_DISPLAY.FEDERAL_WOTC;
  const showFederalNonWotcChip =
    creditTotalsScope === CREDIT_TOTAL_DISPLAY.ALL ||
    creditTotalsScope === CREDIT_TOTAL_DISPLAY.FEDERAL_NON_WOTC;
  const showStateTotalChip =
    creditTotalsScope === CREDIT_TOTAL_DISPLAY.ALL ||
    creditTotalsScope === CREDIT_TOTAL_DISPLAY.STATE;
  const showGrandTotalChip =
    creditTotalsScope === CREDIT_TOTAL_DISPLAY.ALL;
  const empowermentFilterOptions = [
    { value: EMPOWERMENT_FILTER_VALUES.ALL, label: "All Zones" },
    {
      value: EMPOWERMENT_FILTER_VALUES.EMPOWERMENT,
      label: "Federal Empowerment Zone",
    },
    {
      value: EMPOWERMENT_ZONE_TYPES.RURAL,
      label: "Rural Renewal Counties",
    },
    { value: EMPOWERMENT_ZONE_TYPES.NONE, label: "None" },
  ];
  const taxCreditFilterOptions = [
    {
      value: TAX_CREDIT_FILTERS.ALL,
      label: resolveCreditFilterLabel(TAX_CREDIT_FILTERS.ALL),
    },
    {
      value: TAX_CREDIT_FILTERS.FEDERAL_WOTC,
      label: resolveCreditFilterLabel(TAX_CREDIT_FILTERS.FEDERAL_WOTC),
    },
    {
      value: TAX_CREDIT_FILTERS.FEDERAL_NON_WOTC,
      label: resolveCreditFilterLabel(TAX_CREDIT_FILTERS.FEDERAL_NON_WOTC),
    },
    {
      value: TAX_CREDIT_FILTERS.STATE,
      label: resolveCreditFilterLabel(TAX_CREDIT_FILTERS.STATE),
    },
  ];
  const formatCreditTotal = useCallback(
    (value) =>
      typeof value === "number" && !Number.isNaN(value)
        ? value.toLocaleString(undefined, {
            style: "currency",
            currency: "USD",
            maximumFractionDigits: 0,
          })
        : "$0",
    []
  );
  const formatPercentAsDecimal = useCallback((value) => {
    if (typeof value !== "number" || Number.isNaN(value)) {
      return "0.00";
    }
    return value.toFixed(2);
  }, []);
  const renderGraphView = () => {
    if (!visibleBusinesses.length) {
      return (
        <div className="blogs-graph-empty">
          No businesses to visualize. Adjust your filters or load more results.
        </div>
      );
    }
    const totalAmount = Math.max(graphSummary.amounts.total, 0);
    const hasPieData = totalAmount > 0;
    const legend = [
      {
        key: TAX_CREDIT_FILTERS.FEDERAL_WOTC,
        label: "Federal WOTC",
        color: "#1a9c4b",
        amount: graphSummary.amounts.federalWotc,
      },
      {
        key: TAX_CREDIT_FILTERS.FEDERAL_NON_WOTC,
        label: "Federal (Non-WOTC)",
        color: "#0ea5e9",
        amount: graphSummary.amounts.federalNonWotc,
      },
      {
        key: TAX_CREDIT_FILTERS.STATE,
        label: "State Credits",
        color: "#4c6ef5",
        amount: graphSummary.amounts.state,
      },
    ].map((item) => ({
      ...item,
      percent: hasPieData ? (item.amount / totalAmount) * 100 : 0,
    }));
    const pieBackground = hasPieData
      ? buildPieBackgroundFromSegments(legend)
      : "#e4e7f1";
    const graphCardBuilders = [
      () => (
        <div className="blogs-graph-card">
          <div className="blogs-graph-card-title">Credit Mix</div>
          {hasPieData ? (
            <div className="blogs-graph-pie blogs-graph-pie-stacked">
              <div
                className="blogs-graph-pie-chart"
                style={{ background: pieBackground }}
              >
                <span className="blogs-graph-pie-value">
                  {formatCreditTotal(totalAmount)}
                </span>
              </div>
              <div className="blogs-graph-pie-legend">
                {legend.map((item) => (
                  <div key={item.key} className="blogs-graph-pie-legend-item">
                    <span
                      className="blogs-graph-pie-color"
                      style={{ backgroundColor: item.color }}
                    />
                    <span>
                      {item.label} •{" "}
                      <span className="blogs-graph-pie-value-strong">
                        {formatCreditTotal(item.amount)}
                      </span>{" "}
                      ({formatPercentAsDecimal(item.percent)}%)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="blogs-graph-no-data">
              No projected credit totals available yet.
            </div>
          )}
        </div>
      ),
      () => (
        <div className="blogs-graph-card">
          <div className="blogs-graph-card-title">Category Mix</div>
          {categoryDistribution.items.length > 0 ? (
            <div className="blogs-graph-pie blogs-graph-pie-stacked">
              <div
                className="blogs-graph-pie-chart"
                style={{
                  background: buildPieBackgroundFromSegments(
                    categoryDistribution.items
                  ),
                }}
              />
              <div className="blogs-graph-pie-legend">
                {(() => {
                  const hasCategoryAmounts =
                    categoryDistribution.totalAmount > 0;
                  return categoryDistribution.items.map((item) => (
                    <div key={item.key} className="blogs-graph-pie-legend-item">
                      <span
                        className="blogs-graph-pie-color"
                        style={{ backgroundColor: item.color }}
                      />
                      <span>
                        {item.label} •{" "}
                        <span className="blogs-graph-pie-value-strong">
                          {hasCategoryAmounts
                            ? formatCreditTotal(item.amount || 0)
                            : item.count}
                        </span>{" "}
                        ({formatPercentAsDecimal(item.percent)}%)
                      </span>
                    </div>
                  ));
                })()}
              </div>
            </div>
          ) : (
            <div className="blogs-graph-no-data">
              No category data available yet.
            </div>
          )}
        </div>
      ),
      () => (
        <div className="blogs-graph-card">
          <div className="blogs-graph-card-title">Zone Distribution</div>
          {zoneDistribution.total > 0 ? (
            <div className="blogs-graph-pie blogs-graph-pie-stacked">
              <div
                className="blogs-graph-pie-chart"
                style={{
                  background: buildPieBackgroundFromSegments(
                    zoneDistribution.items
                  ),
                }}
              >
                <span className="blogs-graph-pie-value">
                  {zoneDistribution.total}
                </span>
                <span className="blogs-graph-pie-subtext">Businesses</span>
              </div>
              <div className="blogs-graph-pie-legend">
                {zoneDistribution.items.map((item) => (
                  <div key={item.key} className="blogs-graph-pie-legend-item">
                    <span
                      className="blogs-graph-pie-color"
                      style={{ backgroundColor: item.color }}
                    />
                    <span>
                      {item.label} •{" "}
                      <span className="blogs-graph-pie-value-strong">
                        {item.count}
                      </span>{" "}
                      ({formatPercentAsDecimal(item.percent)}%)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="blogs-graph-no-data">
              No businesses available yet.
            </div>
          )}
        </div>
      ),
      () => (
        <div className="blogs-graph-card">
          <div className="blogs-graph-card-title">Industry Mix</div>
          {naicsDistribution.items.length > 0 ? (
            <div className="blogs-graph-pie blogs-graph-pie-stacked">
              <div
                className="blogs-graph-pie-chart"
                style={{
                  background: buildPieBackgroundFromSegments(
                    naicsDistribution.items
                  ),
                }}
              >
                <span className="blogs-graph-pie-value">
                  {naicsDistribution.total}
                </span>
                <span className="blogs-graph-pie-subtext">Businesses</span>
              </div>
              <div className="blogs-graph-pie-legend">
                {naicsDistribution.items.map((item) => (
                  <div key={item.key} className="blogs-graph-pie-legend-item">
                    <span
                      className="blogs-graph-pie-color"
                      style={{ backgroundColor: item.color }}
                    />
                    <span>
                      {item.label} •{" "}
                      <span className="blogs-graph-pie-value-strong">
                        {item.count}
                      </span>{" "}
                      ({formatPercentAsDecimal(item.percent)}%)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="blogs-graph-no-data">
              No NAICS data available yet.
            </div>
          )}
        </div>
      ),
    ];
    const effectiveCardsPerSlide = Math.max(
      1,
      Math.min(cardsPerSlide, graphCardBuilders.length)
    );
    const graphSlides = [];
    for (
      let i = 0;
      i < graphCardBuilders.length;
      i += effectiveCardsPerSlide
    ) {
      graphSlides.push(
        graphCardBuilders.slice(i, i + effectiveCardsPerSlide)
      );
    }
    const maxIndex = Math.max(graphSlides.length - 1, 0);
    const safeGraphSlideIndex = Math.min(graphSlideIndex, maxIndex);
    const currentSlide = graphSlides[safeGraphSlideIndex] || [];
    const totalSlides = graphSlides.length || 1;
    return (
      <div className="blogs-graph-view" ref={graphViewRef}>
        <div className="blogs-graph-summary">
          <div>Total Businesses: {graphSummary.totalBusinesses}</div>
          <div>
            Filtered View: {resolveCreditFilterLabel(activeCreditFilter)}
          </div>
        </div>
        <div className="blogs-graph-carousel">
          <button
            type="button"
            className="blogs-graph-arrow"
            onClick={() => setGraphSlideIndex((prev) => Math.max(prev - 1, 0))}
            disabled={safeGraphSlideIndex === 0}
            aria-label="Previous graph"
          >
            ‹
          </button>
          <div className="blogs-graph-card-wrapper">
            {currentSlide.map((builder, idx) => (
              <div
                key={`graph-card-${safeGraphSlideIndex}-${idx}`}
                className="blogs-graph-card-container"
              >
                {builder()}
              </div>
            ))}
          </div>
          <button
            type="button"
            className="blogs-graph-arrow"
            onClick={() =>
              setGraphSlideIndex((prev) => Math.min(prev + 1, maxIndex))
            }
            disabled={safeGraphSlideIndex === maxIndex}
            aria-label="Next graph"
          >
            ›
          </button>
        </div>
        <div className="blogs-graph-pagination">
          {Math.min(safeGraphSlideIndex + 1, totalSlides)} / {totalSlides}
        </div>
      </div>
    );
  };
  const hasMoreResults =
    !isManualSearch &&
    typeof totalRecords === "number" &&
    totalRecords > 0 && 
    allBusinesses.length > 0 &&
    nextChunkStart <= totalRecords;
  const selectedBusinessIds = useMemo(
    () =>
      selectedBusinesses
        .map((biz) => biz.prospectBusinessId || biz.businessId)
        .filter(Boolean),
    [selectedBusinesses]
  );
  const allVisibleSelected =
    visibleBusinesses.length > 0 &&
    visibleBusinesses.every((biz) => {
      const key = getBusinessSelectionKey(biz);
      return selectedBusinesses.some(
        (item) => getBusinessSelectionKey(item) === key
      );
    });
  const isEmpowermentFilterAll = empowermentFilter === EMPOWERMENT_FILTER_VALUES.ALL;
  const hasAdditionalFilters =
    isNaicsFilterActive ||
    taxCreditFilter !== TAX_CREDIT_FILTERS.ALL ||
    hasCategoryFilters;
  const hasUserAppliedFilters =
    !isEmpowermentFilterAll || hasAdditionalFilters;
  const computedTotalMatches = useMemo(() => {
    if (
      hasEnforcedZones ||
      !isEmpowermentFilterAll ||
      isManualSearch ||
      hasAdditionalFilters
    ) {
      return visibleBusinesses.length;
    }
    if (typeof totalRecords === "number") {
      return totalRecords;
    }
    return visibleBusinesses.length;
  }, [
    hasEnforcedZones,
    hasAdditionalFilters,
    isEmpowermentFilterAll,
    isManualSearch,
    totalRecords,
    visibleBusinesses.length,
  ]);
  const isChunkedDisplayActive =
    !isManualSearch &&
    !hasEnforcedZones &&
    isEmpowermentFilterAll &&
    !hasAdditionalFilters &&
    typeof totalRecords === "number";
  const effectiveResultsCount = isChunkedDisplayActive
    ? displayedResultsCount
    : computedTotalMatches;
  const filtersLocked =
    !isSearchComplete ||
    isLoading ||
    isLoadingMore ||
    (isChunkedDisplayActive && hasMoreResults);
  useEffect(() => {
    if (filtersLocked && isFilterMenuOpen) {
      setFilterMenuOpen(false);
    }
  }, [filtersLocked, isFilterMenuOpen]);
  const handleFilterToggleClick = useCallback(() => {
    if (filtersLocked) return;
    setFilterMenuOpen((prev) => !prev);
  }, [filtersLocked]);

const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);
const downloadMenuRef = useRef(null);
useEffect(() => {
  if (!downloadMenuOpen) return;

  const handler = (e) => {
    if (
      downloadMenuRef.current &&
      !downloadMenuRef.current.contains(e.target)
    ) {
      setDownloadMenuOpen(false);
    }
  };

  document.addEventListener("mousedown", handler);
  return () => document.removeEventListener("mousedown", handler);
}, [downloadMenuOpen]);

const normalizeMapBusinesses = useCallback(
  () =>
    mapBusinessesToShow.map((b) => {
      const id = b.prospectBusinessId || b.businessId;
      const creditInfo = taxCreditData[id] || {};

      return {
        ...b,
        lat: b.lat || b.latitude,
        lng: b.lng || b.longitude,
        taxCredits: [
          {
            totalProjectedTaxCreditAmount:
              creditInfo.projectedCredit,
            availableTaxCredits: creditInfo.availableCredits || [],
          },
        ],
      };
    }),
  [mapBusinessesToShow, taxCreditData]
);

const handleDownloadMapView = useCallback(() => {
  downloadMapAsHtml({
    businesses: normalizeMapBusinesses(),
    dispatch,
    showToastFn: showToast,
  });
}, [normalizeMapBusinesses, dispatch, showToast]);
const handleDownloadMapViewAsPdf = useCallback(() => {
  downloadMapAsPdf({ businesses: normalizeMapBusinesses(), dispatch, showToastFn: showToast });
}, [normalizeMapBusinesses, dispatch]);
const buildCurrentGraphData = useCallback(() => {
  if (!visibleBusinesses.length) return null;

  const totalAmount = Math.max(graphSummary.amounts.total, 0);
  const hasPieData = totalAmount > 0;

  const formatUSD = (n) =>
    Number(n).toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    });

  const creditSegments = [
    {
      label: "Federal WOTC",
      color: "#1a9c4b",
      amount: graphSummary.amounts.federalWotc,
    },
    {
      label: "Federal (Non-WOTC)",
      color: "#0ea5e9",
      amount: graphSummary.amounts.federalNonWotc,
    },
    {
      label: "State Credits",
      color: "#4c6ef5",
      amount: graphSummary.amounts.state,
    },
  ].map((item) => ({
    ...item,
    percent: hasPieData ? (item.amount / totalAmount) * 100 : 0,
  }));

  const hasCategoryAmounts = categoryDistribution.totalAmount > 0;

  const charts = [
    {
      title: "Credit Mix",
      centerLabel: hasPieData ? formatUSD(totalAmount) : "",
      centerSub: null,
      segments: creditSegments.map((s) => ({
        color: s.color,
        percent: s.percent,
      })),
      legend: creditSegments.map((s) => ({
        label: s.label,
        color: s.color,
        value: formatUSD(s.amount),
        percent: s.percent.toFixed(2) + "%",
      })),
    },
    {
      title: "Category Mix",
      centerLabel: "",
      centerSub: null,
      segments: categoryDistribution.items.map((s) => ({
        color: s.color,
        percent: s.percent,
      })),
      legend: categoryDistribution.items.map((s) => ({
        label: s.label,
        color: s.color,
        value: hasCategoryAmounts
          ? formatUSD(s.amount || 0)
          : String(s.count),
        percent: (s.percent || 0).toFixed(2) + "%",
      })),
    },
    {
      title: "Zone Distribution",
      centerLabel: String(zoneDistribution.total),
      centerSub: "Businesses",
      segments: zoneDistribution.items.map((s) => ({
        color: s.color,
        percent: s.percent,
      })),
      legend: zoneDistribution.items.map((s) => ({
        label: s.label,
        color: s.color,
        value: String(s.count),
        percent: (s.percent || 0).toFixed(2) + "%",
      })),
    },
    {
      title: "Industry Mix",
      centerLabel: String(naicsDistribution.total),
      centerSub: "Businesses",
      segments: naicsDistribution.items.map((s) => ({
        color: s.color,
        percent: s.percent,
      })),
      legend: naicsDistribution.items.map((s) => ({
        label: s.label,
        color: s.color,
        value: String(s.count),
        percent: (s.percent || 0).toFixed(2) + "%",
      })),
    },
  ];

  return {
    totalBusinesses: graphSummary.totalBusinesses,
    filteredView: resolveCreditFilterLabel(activeCreditFilter),
    charts,
  };
}, [
  visibleBusinesses,
  graphSummary,
  categoryDistribution,
  zoneDistribution,
  naicsDistribution,
  activeCreditFilter,
]);

// ─────────────────────────────────────────────
// GRAPH DOWNLOADS
// ─────────────────────────────────────────────
const handleDownloadGraphView = useCallback(() => {
  const data = buildCurrentGraphData();
  if (!data) return;

  const html = buildGraphHtml(data);
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  saveAs(blob, "credit-graph.html");
}, [buildCurrentGraphData]);

const handleDownloadGraphViewAsPdf = useCallback(() => {
  const data = buildCurrentGraphData();
  if (!data) return;

  downloadGraphAsPdf(data);
}, [buildCurrentGraphData]);

  // Input for annual hires dialog (when needed for manual search)
  const [askAnnualHires, setAskAnnualHires] = useState(false);
  const [annualHiresInput, setAnnualHiresInput] = useState("");
  const [manualCompanyForAnnualHires, setManualCompanyForAnnualHires] = useState(null);

  useEffect(() => {
    if (!selectedBusinesses.length && isSelectionTrayCollapsed) {
      setSelectionTrayCollapsed(false);
    }
  }, [selectedBusinesses, isSelectionTrayCollapsed]);

  useEffect(() => {
    if (!isChunkedDisplayActive) {
      if (displayedResultsCount !== computedTotalMatches) {
        setDisplayedResultsCount(computedTotalMatches);
      }
      return;
    }
    if (
      typeof totalRecords !== "number" ||
      totalRecords <= 0 ||
      (!fetchedResultCount && !allBusinesses.length)
    ) {
      if (displayedResultsCount !== 0) {
        setDisplayedResultsCount(0);
      }
      return;
    }
    const progressCount = Math.max(allBusinesses.length, fetchedResultCount);
    const resolvedCeiling = Math.max(totalRecords, progressCount);
    const nextCount = Math.min(resolvedCeiling, progressCount);
    if (displayedResultsCount !== nextCount) {
      setDisplayedResultsCount(nextCount);
    }
  }, [
    allBusinesses.length,
    computedTotalMatches,
    displayedResultsCount,
    fetchedResultCount,
    isChunkedDisplayActive,
    totalRecords,
  ]);

  const splitExactAdditionalFromList = (list, businessName, filters = {}) => {
    const searchName = normalizeName(businessName || "");
    const useFilterMatching = filtersIncludeExactCriteria(filters);
    if (useFilterMatching) {
      const exact = [];
      const additional = [];
      (list || []).forEach((item) => {
        if (businessMatchesAllFilters(item, filters)) exact.push(item);
        else additional.push(item);
      });
      return { exact, additional };
    }
    if (!searchName) {
      return { exact: [], additional: list.slice() };
    }
    const exact = [];
    const additional = [];
    (list || []).forEach((item) => {
      if (normalizeName(item.businessName || "") === searchName) exact.push(item);
      else additional.push(item);
    });
    return { exact, additional };
  };

  const renderManualPromptButton = () => (
    <Button
      label="Calculate Tax Credits Manually"
      variant="contained"
      onClick={openManualOnPreviousPage}
      sx={{
        textTransform: "none",
        borderRadius: "8px",
        backgroundColor: "#FFCC33",
        color: "#000",
        boxShadow: "none",
        "&:hover": {
          backgroundColor: "#FFCC33",
          color: "#000",
          boxShadow: "none",
        },
        "&:active": {
          backgroundColor: "#FFCC33",
          color: "#000",
          boxShadow: "none",
        },
        "&:focus": {
          boxShadow: "none",
        },
      }}
    />
  );

  const fetchBusinessChunk = useCallback(
    async (baseParams, start, end, append, searchName) => {
      if (!baseParams || start > end) return [];
      const adjustedEnd = start === 1 && end === 1 ? 2 : end;
      const paramsPayload = {
        ...baseParams,
        businessRangeStart: start,
        businessRangeEnd: adjustedEnd,
      };
      if (
        baseParams.employeeRangeStart !== undefined &&
        baseParams.employeeRangeStart !== ""
      ) {
        paramsPayload.minEmployeeCount = baseParams.employeeRangeStart;
      } else if (baseParams.minEmployeeCount) {
        paramsPayload.minEmployeeCount = baseParams.minEmployeeCount;
      }
      if (
        baseParams.employeeRangeEnd !== undefined &&
        baseParams.employeeRangeEnd !== ""
      ) {
        paramsPayload.maxEmployeeCount = baseParams.employeeRangeEnd;
      } else if (baseParams.maxEmployeeCount) {
        paramsPayload.maxEmployeeCount = baseParams.maxEmployeeCount;
      }
      const config = buildSearchRequestConfig(paramsPayload);
      const headers = buildEmployeeRangeHeaders(baseParams);
      if (Object.keys(headers).length) {
        config.headers = headers;
      }
      const response = await axiosInstance.get(`/businessSearch/V2`, config);
      const data = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.data)
        ? response.data.data
        : [];
      const sortedBusinesses = sortBusinessesByRelevance(data, searchName);
      setAllBusinesses((prev) => {
        const combined = append ? [...prev, ...sortedBusinesses] : sortedBusinesses;
        return dedupeBusinesses(combined);
      });
      setFetchedResultCount((prev) => Math.max(prev, end));
      setNextChunkStart(end + 1);
      setIsSearchComplete(true);
      return sortedBusinesses;
    },
    []
  );

  const loadNextChunk = useCallback(async () => {
    if (isLoadingMore || !baseParamsRef.current) return;
    if (totalRecords && nextChunkStart > totalRecords) return;
    const chunkEnd = Math.min(
      nextChunkStart + CHUNK_SIZE - 1,
      totalRecords || nextChunkStart + CHUNK_SIZE - 1
    );
    setIsLoadingMore(true);
    try {
      await fetchBusinessChunk(
        baseParamsRef.current,
        nextChunkStart,
        chunkEnd,
        true,
        filters?.businessName
      );
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    fetchBusinessChunk,
    filters?.businessName,
    isLoadingMore,
    nextChunkStart,
    totalRecords,
  ]);

  useEffect(() => {
    if (isManualSearch || hasEnforcedZones) return;
    if (!isChunkedDisplayActive) return;
    if (!hasMoreResults) return;
    if (isLoadingMore) return;
    loadNextChunk();
  }, [
    hasMoreResults,
    hasEnforcedZones,
    isChunkedDisplayActive,
    isLoadingMore,
    isManualSearch,
    loadNextChunk,
  ]);

  const openManualOnPreviousPage = () => {
    try {
      sessionStorage.setItem(
        "openManualFromResults",
        JSON.stringify({ openManual: true, prefill: filters || {} })
      );
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate(`${roleBasePath}/findBusinavTab`, {
          state: { openManual: true, prefill: filters || {} },
        });
      }
    } catch (e) {
      console.error("openManualOnPreviousPage error:", e);
      navigate(`${roleBasePath}/findBusinavTab`, {
        state: { openManual: true, prefill: filters || {} },
      });
    }
  };

  useEffect(() => {
    const isManualSearchRequest =
      isManualSearch || (location.state?.companies && !filters?.state);
    const shouldFetchFilterResults =
      hasSearchFilters(filters) && !isManualSearchRequest;

    if (shouldFetchFilterResults) {
      setIsSearchComplete(false);
      setSelectedBusinesses([]);
      clearTaxCreditCache();
      setTaxCreditData({});
      setTaxCreditLoading({});
      const baseParams = {
        BusinessName: filters.businessName,
        Keyword: filters.industry,
        StreetAddress: filters.streetAddress,
        City: filters.city,
        State: filters.state,
        Zip: filters.zip,
        BusinessNameSearchType: isEaxctNameSearchSelected ? "ExactName" : "StartsWith",
        searchInProximity: isProximityBasedCount,
      };
      if (Array.isArray(filters.naicsCodes) && filters.naicsCodes.length) {
        baseParams.NAICS = filters.naicsCodes;
      }
      if (Array.isArray(filters.censusCodes) && filters.censusCodes.length) {
        baseParams.CensusCodes = filters.censusCodes;
      }
      const normalizeEmployeeValue = (value) => {
        if (value === null || typeof value === "undefined") return "";
        return String(value).trim();
      };
      const filterMinValue = normalizeEmployeeValue(
        filters.employeeMin ?? filters.minEmployeeCount
      );
      const filterMaxValue = normalizeEmployeeValue(
        filters.employeeMax ?? filters.maxEmployeeCount
      );
      if (filterMinValue !== "") {
        baseParams.minEmployeeCount = filterMinValue;
        baseParams.employeeRangeStart = filterMinValue;
      }
      if (filterMaxValue !== "") {
        baseParams.maxEmployeeCount = filterMaxValue;
        baseParams.employeeRangeEnd = filterMaxValue;
      }
      baseParamsRef.current = baseParams;
      setNextChunkStart(1);
      const fetchAllBusinesses = async () => {
        try {
          setLoader(true);
          setAllBusinesses([]);
          setFetchedResultCount(0);
          let rawCount =
            typeof passedRecordCount === "number" ? passedRecordCount : 0;

          if (!rawCount) {
            const countConfig = buildSearchRequestConfig(baseParams);
            const headers = buildEmployeeRangeHeaders(baseParams);
            if (Object.keys(headers).length) {
              countConfig.headers = headers;
            }
            const countResponse = await axiosInstance.get(
              `/businessSearch/count`,
              countConfig
            );
            rawCount = Number(countResponse.data.recordCount || 0);
          }

          setTotalRecords(rawCount);

          if (!rawCount) {
            setIsSearchComplete(true);
            setNextChunkStart(1);
            setFetchedResultCount(0);
            setLoader(false);
            return;
          }

          const chunkEnd = Math.min(CHUNK_SIZE, rawCount);
          await fetchBusinessChunk(
            baseParams,
            1,
            chunkEnd,
            false,
            filters.businessName
          );
        } catch {
          setAllBusinesses([]);
          setTotalRecords(0);
          setFetchedResultCount(0);
          setIsSearchComplete(true);
        } finally {
          setLoader(false);
        }
      };
      fetchAllBusinesses();
    } else if (isManualSearchRequest) {
      const sourceList =
        (location.state && location.state.companies) || companies || [];

      setIsSearchComplete(false);
      setSelectedBusinesses([]);

      // --- If no annual hires present, prompt for it from user ---
      if (
        Array.isArray(sourceList) &&
        sourceList.length > 0 &&
        typeof (manualAnnualHires ?? sourceList[0].annualHires) === "undefined"
      ) {
        setAskAnnualHires(true);
        setManualCompanyForAnnualHires(sourceList[0]);
        setAllBusinesses([]);
        setTotalRecords(0);
        setFetchedResultCount(0);
        return;
      }
      let processedList = sourceList;
      if (Array.isArray(sourceList) && sourceList.length > 0 && sourceList[0].prospectBusinessId) {
        processedList = sourceList.map(item => ({
          ...item,
          businessId: item.prospectBusinessId,
          businessName: item.businessName || '',
          state: item.physicalState || item.state || '',
          physicalAddress: item.physicalAddress || item.address || '',
          physicalCity: item.physicalCity || item.city || '',
          physicalZip: item.physicalZip || item.zip || '',
          locationEmployeesCount: item.locationEmployeesCount || 0,
          annualHires: manualAnnualHires ?? item.annualHires ?? 0,
          taxCredits: [{
            totalProjectedTaxCreditAmount: item.totalProjectedTaxCreditAmount,
            availableTaxCredits: item.availableTaxCredits || []
          }]
        }));
      }
      const sortedSource = sortBusinessesByRelevance(
        processedList,
        filters?.businessName
      );
      setAllBusinesses(dedupeBusinesses(sortedSource));
      setTotalRecords(sortedSource.length);
      setNextChunkStart(sortedSource.length + 1);
      setFetchedResultCount(sortedSource.length);
      baseParamsRef.current = null;
      setIsSearchComplete(true);

      if (Array.isArray(sourceList) && sourceList.length > 0) {
        sourceList.forEach(item => {
          if (item.prospectBusinessId && (item.totalProjectedTaxCreditAmount !== undefined || item.availableTaxCredits)) {
            const availableCredits = Array.isArray(item.availableTaxCredits)
              ? item.availableTaxCredits
              : [];
            const creditData = {
              projectedCredit: item.totalProjectedTaxCreditAmount,
              eligibleCredits: availableCredits.map(c => c?.name).filter(Boolean),
              availableCredits,
            };
            taxCreditCache[item.prospectBusinessId] = creditData;
            setTaxCreditData(prev => ({
              ...prev,
              [item.prospectBusinessId]: creditData
            }));
          }
        });
      }
    }
  }, [
    filters,
    isProximityBasedCount,
    isEaxctNameSearchSelected,
    companies,
    location.state,
    passedRecordCount,
    isManualSearch,
    manualAnnualHires,
    fetchBusinessChunk
  ]);

  const handleAnnualHiresSubmit = async (e) => {
    e.preventDefault();
    if (!annualHiresInput || isNaN(Number(annualHiresInput)) || Number(annualHiresInput) < 0) {
      setError("Please enter a valid annual hires value.");
      return;
    }
    setError(null);
    setAskAnnualHires(false);
    try {
      setLoader(true);
      const lookupPayload = [{
        prospectBusinessId: manualCompanyForAnnualHires.prospectBusinessId,
        annualHiresOverride: Number(annualHiresInput)
      }];
      const lookupResponse = await axiosInstance.post('/taxCredits/lookup/v2', lookupPayload);
      const matched = lookupResponse.data.find(
        (item) => item.prospectBusinessId === manualCompanyForAnnualHires.prospectBusinessId
      );
      let businessWithTaxCredits = {
        ...manualCompanyForAnnualHires,
        annualHires: Number(annualHiresInput),
        taxCredits: matched ? [{
          totalProjectedTaxCreditAmount: matched.totalProjectedTaxCreditAmount,
          availableTaxCredits: matched.availableTaxCredits || []
        }] : []
      };
      navigate(`?page=1`, {
        state: {
          ...location.state,
          companies: [businessWithTaxCredits],
          annualHires: Number(annualHiresInput),
          isManualSearch: true
        }
      });
    } catch (err) {
      setError("Failed to calculate tax credits. Please try again.");
      setAskAnnualHires(false);
      setLoader(false);
    }
  };

  const extractLookupData = (response) => {
    if (!response) return [];
    if (Array.isArray(response.data)) return response.data;
    if (Array.isArray(response.data?.data)) return response.data?.data;
    return [];
  };

  const fetchTaxCreditForBusiness = useCallback(async (business, overrideAnnualHires) => {
    if (!business?.prospectBusinessId) return;
    const forceLookup =
      typeof overrideAnnualHires === "number" && !Number.isNaN(overrideAnnualHires);
    if (isManualSearch && taxCreditData[business.prospectBusinessId] && !forceLookup) {
      return;
    }
    if (!forceLookup && taxCreditCache[business.prospectBusinessId]) {
      setTaxCreditData((prev) => ({
        ...prev,
        [business.prospectBusinessId]: taxCreditCache[business.prospectBusinessId],
      }));
      setTaxCreditLoading((prev) => ({
        ...prev,
        [business.prospectBusinessId]: false,
      }));
      return;
    }
    setTaxCreditLoading((prev) => ({
      ...prev,
      [business.prospectBusinessId]: true,
    }));
    try {
      const resolvedAnnualHires =
        typeof overrideAnnualHires === "number" && !Number.isNaN(overrideAnnualHires)
          ? overrideAnnualHires
          : Number(business.annualHires || 0);
      const payload = [
        {
          prospectBusinessId: business.prospectBusinessId,
          annualHiresOverride: resolvedAnnualHires,
        },
      ];
      const response = await axiosInstance.post('/taxCredits/lookup/v2', payload);
      const lookupData = extractLookupData(response);
      const matched = lookupData.find(
        (item) => item.prospectBusinessId === business.prospectBusinessId
      );
      let projectedCredit;
      let eligibleCredits = [];
      let availableCredits = [];
      if (matched) {
        projectedCredit = matched.totalProjectedTaxCreditAmount;
        availableCredits = Array.isArray(matched.availableTaxCredits)
          ? matched.availableTaxCredits
          : [];
        eligibleCredits = availableCredits.map((c) => c?.name).filter(Boolean);
      }
      const creditData = {
        projectedCredit,
        eligibleCredits,
        availableCredits,
      };
      taxCreditCache[business.prospectBusinessId] = creditData;
      setTaxCreditData((prev) => ({
        ...prev,
        [business.prospectBusinessId]: creditData,
      }));
      setTaxCreditLoading((prev) => ({
        ...prev,
        [business.prospectBusinessId]: false,
      }));
      return creditData;
    } catch (err) {
      setTaxCreditLoading((prev) => ({
        ...prev,
        [business.prospectBusinessId]: false,
      }));
      return null;
    }
  }, [isManualSearch, taxCreditData]);

  useEffect(() => {
    if (isManualSearch) return;
    mapBusinessesToShow.forEach((item) => {
      if (
        item?.prospectBusinessId &&
        !taxCreditData[item.prospectBusinessId] &&
        !taxCreditLoading[item.prospectBusinessId]
      ) {
        fetchTaxCreditForBusiness(item);
      }
    });
  }, [
    mapBusinessesToShow,
    fetchTaxCreditForBusiness,
    taxCreditData,
    taxCreditLoading,
    isManualSearch,
  ]);

  useEffect(() => {
    if (!Array.isArray(allBusinesses) || !allBusinesses.length) return;
    setSelectedBusinesses((prev) => {
      if (!prev.length) return prev;
      return prev.map((selected) => {
        const updated = allBusinesses.find(
          (biz) => getBusinessSelectionKey(biz) === getBusinessSelectionKey(selected)
        );
        return updated ? buildBusinessForNavigation(updated) : selected;
      });
    });
  }, [allBusinesses]);

  const startEditingAnnualHires = (businessId, currentValue) => {
    setAnnualHiresDrafts((prev) => ({
      ...prev,
      [businessId]:
        currentValue !== null && currentValue !== undefined
          ? String(currentValue)
          : "",
    }));
  };

  const cancelEditingAnnualHires = (businessId) => {
    setAnnualHiresDrafts((prev) => {
      const { [businessId]: _, ...rest } = prev;
      return rest;
    });
  };

  const handleAnnualHiresInputChange = (businessId, value) => {
    setAnnualHiresDrafts((prev) => ({
      ...prev,
      [businessId]: value,
    }));
  };

  const handleAnnualHiresSave = async (business) => {
    const businessId = business?.prospectBusinessId;
    if (!businessId) return;
    const draftValue = annualHiresDrafts[businessId];
    const trimmedValue =
      typeof draftValue === "string" ? draftValue.trim() : draftValue;
    if (trimmedValue === "" || trimmedValue === null || trimmedValue === undefined) {
      dispatch(
        showToast({
          type: "error",
          message: "Please enter a valid annual hires value.",
        })
      );
      return;
    }
    const hiresValue = Number(trimmedValue);
    if (Number.isNaN(hiresValue) || hiresValue < 0) {
      dispatch(
        showToast({
          type: "error",
          message: "Please enter a valid annual hires value.",
        })
      );
      return;
    }
    setAnnualHiresSaving((prev) => ({
      ...prev,
      [businessId]: true,
    }));
    try {
      const updated = await fetchTaxCreditForBusiness(business, hiresValue);
      if (!updated) {
        dispatch(
          showToast({
            type: "error",
            message: "Lookup request failed. Please try again.",
          })
        );
        return;
      }
      setAnnualHiresOverrides((prev) => ({
        ...prev,
        [businessId]: hiresValue,
      }));
      setAllBusinesses((prev) =>
        prev.map((item) => {
          if (item?.prospectBusinessId !== businessId) return item;
          const copy = { ...(item || {}) };
          copy.annualHires = hiresValue;
          copy.annualHiresOverride = hiresValue;
          copy.taxCredits = [
            {
              totalProjectedTaxCreditAmount:
                typeof updated.projectedCredit !== "undefined"
                  ? updated.projectedCredit
                  : copy.taxCredits?.[0]?.totalProjectedTaxCreditAmount ?? null,
              availableTaxCredits:
                Array.isArray(updated.availableCredits) && updated.availableCredits.length
                  ? updated.availableCredits
                  : (updated.eligibleCredits || []).map((name) =>
                      typeof name === "string" ? { name } : name
                    ),
            },
          ];
          return copy;
        })
      );
      cancelEditingAnnualHires(businessId);
      dispatch(
        showToast({
          type: "success",
          message: "Annual hires updated.",
        })
      );
    } finally {
      setAnnualHiresSaving((prev) => ({
        ...prev,
        [businessId]: false,
      }));
    }
  };

  const resolveAnnualHiresValue = (business) => {
    if (!business?.prospectBusinessId) return business?.annualHires;
    const override = annualHiresOverrides[business.prospectBusinessId];
    if (override !== undefined) {
      return override;
    }
    return business?.annualHires;
  };

  const resolveTaxCreditsForBusiness = (business) => {
    if (!business?.prospectBusinessId) return business?.taxCredits;
    const cached = taxCreditData[business.prospectBusinessId];
    if (cached) {
      return [
        {
          totalProjectedTaxCreditAmount:
            typeof cached.projectedCredit !== "undefined"
              ? cached.projectedCredit
              : null,
          availableTaxCredits:
            Array.isArray(cached.availableCredits) && cached.availableCredits.length
              ? cached.availableCredits
              : (cached.eligibleCredits || []).map((name) =>
                  typeof name === "string" ? { name } : name
                ),
        },
      ];
    }
    return business?.taxCredits;
  };

  const buildBusinessForNavigation = (business) => {
    if (!business) return business;
    const copy = { ...(business || {}) };
    const resolvedHires = resolveAnnualHiresValue(business);
    if (resolvedHires !== undefined) {
      copy.annualHires = resolvedHires;
      copy.annualHiresOverride = resolvedHires;
    }
    const resolvedCredits = resolveTaxCreditsForBusiness(business);
    if (resolvedCredits) {
      copy.taxCredits = resolvedCredits;
    }
    return copy;
  };

  const enrichBusinessesWithTaxCredits = (list) => {
    if (!Array.isArray(list)) return [];
    return list.map((biz) => {
      const copy = { ...(biz || {}) };
      if (Array.isArray(copy.taxCredits) && copy.taxCredits.length > 0) {
        return copy;
      }
      const cached = copy.prospectBusinessId ? taxCreditData[copy.prospectBusinessId] : null;
      if (cached) {
        copy.taxCredits = [
          {
            totalProjectedTaxCreditAmount: typeof cached.projectedCredit !== "undefined" ? cached.projectedCredit : null,
            availableTaxCredits:
              Array.isArray(cached.availableCredits) && cached.availableCredits.length
                ? cached.availableCredits
                : (cached.eligibleCredits || []).map((name) =>
                    typeof name === "string" ? { name } : name
                  ),
          },
        ];
        return copy;
      }
      return copy;
    });
  };

  const sanitizeBusinessesForExport = (list) => {
    if (!Array.isArray(list)) return [];
    return list.map((item) => {
      const copy = { ...(item || {}) };
      if (!Array.isArray(copy.taxCredits) || copy.taxCredits.length === 0) {
        copy.taxCredits = [
          {
            availableTaxCredits: [],
            totalProjectedTaxCreditAmount: null,
          },
        ];
      } else {
        const first = copy.taxCredits[0] || {};
        if (!Array.isArray(first.availableTaxCredits)) {
          copy.taxCredits[0] = {
            ...(first || {}),
            availableTaxCredits: [],
          };
        }
        if (typeof copy.taxCredits[0].totalProjectedTaxCreditAmount === "undefined") {
          copy.taxCredits[0].totalProjectedTaxCreditAmount = null;
        }
      }
      copy.businessName = copy.businessName || "";
      copy.physicalAddress = copy.physicalAddress || "";
      copy.physicalCity = copy.physicalCity || "";
      copy.state = copy.state || "";
      copy.physicalZip = copy.physicalZip || "";
      copy.locationEmployeesCount =
        typeof copy.locationEmployeesCount === "number"
          ? copy.locationEmployeesCount
          : copy.locationEmployeesCount || "";
    const overrideValue =
      (copy.prospectBusinessId &&
        Object.prototype.hasOwnProperty.call(annualHiresOverrides, copy.prospectBusinessId)
        ? annualHiresOverrides[copy.prospectBusinessId]
        : undefined) ?? copy.annualHires;
    copy.annualHires =
      typeof overrideValue === "number" ? overrideValue : overrideValue || "";
      return copy;
    });
  };

const ensureExportArrayFields = (rows) => {
  if (!Array.isArray(rows)) return [];
  return rows.map((r) => {
      const row = { ...(r || {}) };
      if (Object.prototype.hasOwnProperty.call(row, "Credits identified")) {
        if (!Array.isArray(row["Credits identified"])) {
          if (row["Credits identified"] == null) {
            row["Credits identified"] = [];
          } else if (typeof row["Credits identified"] === "string") {
            const parts = row["Credits identified"].split(",").map((s) => s.trim()).filter(Boolean);
            row["Credits identified"] = parts.length ? parts : [row["Credits identified"]];
          } else {
            row["Credits identified"] = [row["Credits identified"]];
          }
        }
      } else if (Object.prototype.hasOwnProperty.call(row, "Credits Identified")) {
        const val = row["Credits Identified"];
        delete row["Credits Identified"];
        row["Credits identified"] = Array.isArray(val) ? val : val == null ? [] : (typeof val === "string" ? val.split(",").map((s) => s.trim()).filter(Boolean) : [val]);
      } else {
        row["Credits identified"] = row["Credits identified"] || [];
      }
      const otherArrayKeys = ["Eligible Credits", "eligibleCredits", "Available Credits"];
      otherArrayKeys.forEach((k) => {
        if (Object.prototype.hasOwnProperty.call(row, k) && !Array.isArray(row[k])) {
          if (row[k] == null) row[k] = [];
          else if (typeof row[k] === "string") row[k] = row[k].split(",").map((s) => s.trim()).filter(Boolean);
          else row[k] = [row[k]];
        }
      });
      return row;
  });
};

  const handleExportAllResults = async () => {
    const source = Array.isArray(allBusinesses) ? allBusinesses : [];
    if (!source.length) return;
    try {
      const enriched = enrichBusinessesWithTaxCredits(source);
      const sanitizedSource = sanitizeBusinessesForExport(enriched);
      const jsonData = await prepareRequiredJsonToExcelDownload(sanitizedSource);
      const finalData = ensureExportArrayFields(jsonData);
      const heading = resolveVersionHeading(enriched);
      const fileName = buildVersionedFileName(
        "Business_Search_Results",
        enriched
      );
      convertJsonToExcel(finalData, fileName, { heading });
    } catch (err) {
      console.error("Export error:", err);
      dispatch(
        showToast({
          type: "error",
          message: "Failed to export. Please try again.",
        })
      );
    }
  };

  const toggleBusinessSelection = (business) => {
    if (!business) return;
    const key = getBusinessSelectionKey(business);
    if (!key) return;
    setSelectedBusinesses((prev) => {
      const exists = prev.some(
        (item) => getBusinessSelectionKey(item) === key
      );
      if (exists) {
        return prev.filter(
          (item) => getBusinessSelectionKey(item) !== key
        );
      }
      return [...prev, buildBusinessForNavigation(business)];
    });
  };

  const removeSelectedBusiness = (business) => {
    if (!business) return;
    const key = getBusinessSelectionKey(business);
    setSelectedBusinesses((prev) =>
      prev.filter((item) => getBusinessSelectionKey(item) !== key)
    );
  };

  const downloadSelectedAsExcel = async () => {
    if (!selectedBusinesses.length) {
      dispatch(
        showToast({
          type: "error",
          message: "Select at least one business to export.",
        })
      );
      return;
    }
    try {
      const enriched = enrichBusinessesWithTaxCredits(selectedBusinesses);
      const sanitized = sanitizeBusinessesForExport(enriched);
      const jsonData = await prepareRequiredJsonToExcelDownload(sanitized);
      const finalData = ensureExportArrayFields(jsonData);
      const heading = resolveVersionHeading(enriched);
      const fileName = buildVersionedFileName(
        "Selected_Businesses",
        enriched
      );
      convertJsonToExcel(finalData, fileName, { heading });
    } catch (error) {
      console.error("Selected export error:", error);
      dispatch(
        showToast({
          type: "error",
          message: "Failed to export selected businesses.",
        })
      );
    }
  };

  const downloadSelectedAsPdf = async () => {
    if (!selectedBusinesses.length) {
      dispatch(
        showToast({
          type: "error",
          message: "Select at least one business to export.",
        })
      );
      return;
    }

    const TEMP_ID = "blogs-combined-pdf-preview-content";
    const cleanup = () => { document.getElementById(TEMP_ID)?.remove(); };

    setIsPdfDownloading(true);
    try {
      await waitForNextFrame();

      const headerOpts = {
        userName: appUserName,
        logoUrl: canaryLogo,
        date: dayjs().format("M/D/YYYY"),
      };

      const mapBusinesses = normalizeMapBusinesses();
      const graphData = buildCurrentGraphData();

      const [rawMap, rawGraph] = await Promise.all([
        renderMapCanvas(mapBusinesses),
        Promise.resolve(graphData ? renderGraphCanvas(graphData) : null),
      ]);
      const [mapResult, graphResult] = await Promise.all([
        rawMap ? composeWithHeader(rawMap, headerOpts) : Promise.resolve(null),
        rawGraph ? composeWithHeader(rawGraph, headerOpts) : Promise.resolve(null),
      ]);

      const mapDataUrl = mapResult ? mapResult.canvas.toDataURL("image/jpeg", 0.92) : null;
      const graphDataUrl = graphResult ? graphResult.canvas.toDataURL("image/jpeg", 0.92) : null;

      const detailEl = document.getElementById("blogs-selected-pdf-content");
      const detailHtml = detailEl ? detailEl.innerHTML : "";

      const combinedHtml = `
        <style>
          .pdf-canvas-page {
            width: 100%;
            background: #fff;
            page-break-after: always;
            break-after: page;
            box-sizing: border-box;
          }
          .pdf-canvas-page img { width: 100%; height: auto; display: block; }
          .pdf-detail-pages { background: #fff; }
        </style>
        ${mapDataUrl ? `<div class="pdf-canvas-page"><img src="${mapDataUrl}" /></div>` : ""}
        ${graphDataUrl ? `<div class="pdf-canvas-page"><img src="${graphDataUrl}" /></div>` : ""}
        <div class="pdf-detail-pages">${detailHtml}</div>
      `;

      cleanup();
      const tempEl = document.createElement("div");
      tempEl.id = TEMP_ID;
      Object.assign(tempEl.style, {
        position: "absolute", left: "-9999px", top: "0",
        background: "#fff", width: "1024px",
      });
      tempEl.innerHTML = combinedHtml;
      document.body.appendChild(tempEl);

      await openPrintPreviewFromTemplate(TEMP_ID, {
        title: `Tax Credits Analysis — ${dayjs().format("M/D/YYYY")}`,
      });
    } catch (error) {
      const errorMessage = error?.message && typeof error.message === "string" ? error.message : "";
      if (errorMessage.toLowerCase().includes("print preview was closed")) {
        return;
      }
      dispatch(
        showToast({
          type: "error",
          message: errorMessage || "Failed to open preview. Please try again.",
        })
      );
    } finally {
      cleanup();
      setIsPdfDownloading(false);
    }
  };

  const backNavigation = () => {
    if (currentRole === "Salesperson") {
      navigate("/salesperson/findBusinavTab");
    } else {
      navigate(`${roleBasePath}/dashboard/findbusiness`);
    }
  };

  // ============ Annual Hires Prompt ==========
  if (askAnnualHires && manualCompanyForAnnualHires) {
    return (
      <div style={{ maxWidth: 360, margin: "60px auto", padding: 32, border: "1px solid #ccc", borderRadius: 15, background: "#fff" }}>
        <h3>Enter Annual Hires</h3>
        <div style={{ marginBottom: 8 }}>
          Please input the annual hires for <b>{stripHtml(manualCompanyForAnnualHires.businessName)}</b>:
        </div>
        <form onSubmit={handleAnnualHiresSubmit}>
          <input
            type="number"
            style={{ width: "100%", margin: "12px 0", padding: 8, fontSize: 16 }}
            value={annualHiresInput}
            min={0}
            step={1}
            onChange={e => setAnnualHiresInput(e.target.value)}
            placeholder="Enter number of annual hires"
            required
          />
          {error && <div style={{ color: "red", marginBottom: 8 }}>{error}</div>}
          <Button
            type="submit"
            variant="contained"
            label={isLoading ? "Calculating..." : "Submit"}
            disabled={isLoading}
          />
        </form>
      </div>
    );
  }


  // Helper to truncate eligible credits for chip UI
  const truncateCredit = (credit) => {
    if (!credit) return "";
    const words = credit.split(" ");
    return words.length > 2
      ? words.slice(0, 2).join(" ") + "..."
      : credit;
  };

  const isBusinessSelected = (business) => {
    if (!business) return false;
    const key = getBusinessSelectionKey(business);
    return selectedBusinesses.some(
      (item) => getBusinessSelectionKey(item) === key
    );
  };

  const handleToggleVisibleSelection = () => {
    if (!visibleBusinesses.length) return;
    if (allVisibleSelected) {
      setSelectedBusinesses((prev) =>
        prev.filter((item) => {
          const itemKey = getBusinessSelectionKey(item);
          return !visibleBusinesses.some(
            (biz) => getBusinessSelectionKey(biz) === itemKey
          );
        })
      );
    } else {
      setSelectedBusinesses((prev) => {
        const currentKeys = new Set(
          prev.map((item) => getBusinessSelectionKey(item))
        );
        const additions = visibleBusinesses
          .map((biz) => buildBusinessForNavigation(biz))
          .filter(
            (biz) => !currentKeys.has(getBusinessSelectionKey(biz))
          );
        return [...prev, ...additions];
      });
    }
  };

  const renderCard = (post, index) => {
    const businessId = post.prospectBusinessId;
    const selectionKey = getBusinessSelectionKey(post);
    const isSelected = isBusinessSelected(post);
    const employeesDisplay = formatEmployeesDisplay(post);
    const naicsDisplay = post?.naics || post?.naicS01 || "_";
    const censusTractDisplay =
      post?.censusTract || post?.censusTractCode || post?.CensusTract || "_";
    const zoneCategory = getEmpowermentZoneCategory(post.empowermentZone);
    const zoneLabels = getEmpowermentZoneLabels(post.empowermentZone);
    const hasMultipleZones = zoneLabels.length > 1;
    const cardClasses = [
      "result-card",
      "partitioned-card",
      isSelected ? "selected-card" : "",
    ];
    if (zoneCategory === EMPOWERMENT_ZONE_TYPES.EMPOWERMENT) {
      cardClasses.push("card-empowerment");
    } else if (zoneCategory === EMPOWERMENT_ZONE_TYPES.RURAL) {
      cardClasses.push("card-rural");
    }
    const renderAnnualHiresField = () => {
      const displayAnnualHires =
        annualHiresOverrides[businessId] ?? post?.annualHires;
      const formattedAnnualHires =
        displayAnnualHires !== null &&
        displayAnnualHires !== undefined &&
        displayAnnualHires !== ""
          ? Number(displayAnnualHires).toLocaleString(undefined, {
              maximumFractionDigits: 2,
            })
          : null;
      const isEditing = Object.prototype.hasOwnProperty.call(
        annualHiresDrafts,
        businessId
      );
      const draftValue = annualHiresDrafts[businessId] ?? "";
      const saving = annualHiresSaving[businessId];
      if (isEditing) {
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <input
              type="number"
              min="0"
              step="0.1"
              value={draftValue}
              onChange={(e) =>
                handleAnnualHiresInputChange(
                  businessId,
                  e.target.value
                )
              }
              style={{
                width: 130,
                padding: "4px 6px",
                borderRadius: 4,
                border: "1px solid #ccc",
              }}
            />
            <LoadingButton
              size="small"
              variant="outlined"
              loading={!!saving}
              onClick={() => handleAnnualHiresSave(post)}
              sx={{
                textTransform: "none",
                minWidth: 64,
                height: 32,
                padding: "0 12px",
              }}
            >
              Save
            </LoadingButton>
            <button
              type="button"
              onClick={() => cancelEditingAnnualHires(businessId)}
              style={{
                background: "none",
                border: "none",
                color: "#0066cc",
                cursor: "pointer",
                textDecoration: "underline",
                padding: 0,
              }}
            >
              Cancel
            </button>
          </div>
        );
      }
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <b>
            {formattedAnnualHires !== null
              ? formattedAnnualHires
              : "No Data"}
          </b>
          <button
            type="button"
            onClick={() =>
              startEditingAnnualHires(businessId, displayAnnualHires)
            }
            style={{
              background: "none",
              border: "none",
              color: "#0066cc",
              cursor: "pointer",
              textDecoration: "underline",
              padding: 0,
            }}
          >
            Edit
          </button>
        </div>
      );
    };
    const creditObj =
      (businessId && taxCreditData[businessId]) ||
      extractTaxCreditFields(post);
    const creditDisplayData = buildCreditDisplayData(post, taxCreditData);
    const applyCategoryFilterToEntries = (entries = []) => {
      if (!categoryFilterSet || !categoryFilterSet.size) {
        return entries;
      }
      return entries.filter(
        (entry) => entry.categoryKey && categoryFilterSet.has(entry.categoryKey)
      );
    };
    const categoryScopedEntries = applyCategoryFilterToEntries(
      creditDisplayData.entries
    );
    const scopedCreditEntries =
      activeCreditFilter === TAX_CREDIT_FILTERS.ALL
        ? categoryScopedEntries
        : categoryScopedEntries.filter(
            (entry) => entry.type === activeCreditFilter
          );
    const creditNamesForScope = scopedCreditEntries
      .map((entry) => entry.label)
      .filter(Boolean);

    // Show loading... instead of No Data until fetched
    const loadingTaxCredit = businessId && taxCreditLoading[businessId];

    // Show loading only if we don't have a value and we're loading
    const showProjectedLoading =
      typeof creditObj.projectedCredit === "undefined" && loadingTaxCredit;
    const showEligibleLoading =
      (!creditObj.eligibleCredits || !Array.isArray(creditObj.eligibleCredits)) &&
      loadingTaxCredit;
    const filteredProjectedCredit = (() => {
      if (categoryFilterSet && categoryFilterSet.size) {
        return scopedCreditEntries.reduce(
          (sum, entry) => sum + entry.amount,
          0
        );
      }
      if (activeCreditFilter === TAX_CREDIT_FILTERS.FEDERAL_WOTC) {
        return creditDisplayData.totals.federalWotc;
      }
      if (activeCreditFilter === TAX_CREDIT_FILTERS.FEDERAL_NON_WOTC) {
        return creditDisplayData.totals.federalNonWotc;
      }
      if (activeCreditFilter === TAX_CREDIT_FILTERS.STATE) {
        return creditDisplayData.totals.state;
      }
      return creditDisplayData.totals.total;
    })();
    const resolvedProjectedCredit = (() => {
      if (
        creditDisplayData.hasProjectionValue &&
        typeof filteredProjectedCredit === "number" &&
        !Number.isNaN(filteredProjectedCredit)
      ) {
        return filteredProjectedCredit;
      }
      if (
        typeof creditObj.projectedCredit !== "undefined" &&
        creditObj.projectedCredit !== null
      ) {
        const parsed = parseCreditAmountValue(creditObj.projectedCredit);
        return parsed !== null ? parsed : null;
      }
      return null;
    })();
    const totalEligibleCredits = creditNamesForScope.length;
    const hasMoreEligibleCredits = totalEligibleCredits > 3;
    const visibleEligibleCredits = hasMoreEligibleCredits
      ? creditNamesForScope.slice(0, 3)
      : creditNamesForScope;
    const extraEligibleCredits = Math.max(
      0,
      totalEligibleCredits - visibleEligibleCredits.length
    );

    return (
      <div
        className={cardClasses.join(" ").trim()}
        key={index}
        style={{ cursor: "default" }}
      >
        <div className="result-card-body">
          <div className="result-card-content">
            <div className="result-card-header">
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="result-card-title">
                  {stripHtml(post.businessName)}
                </div>
              </div>
              <div className="result-card-header-actions">
                <a
                  href={`https://${post.url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="result-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  <img
                    src={Link}
                    alt="link"
                    style={{ width: 18, height: 18, verticalAlign: "middle" }}
                  />
                </a>
                <Checkbox
                  checked={isSelected}
                  onChange={(e) => {
                    e.stopPropagation();
                    toggleBusinessSelection(post);
                  }}
                  name={`select-${selectionKey}`}
                />
              </div>
            </div>
            <div className="result-card-address">
              {post.physicalAddress}
              {post.physicalCity ? `, ${post.physicalCity}` : ""}
              {post.state ? `, ${post.state}` : ""}
            </div>
            {post.siC01Description && (
              <div className="result-card-details">
                Category: {post.siC01Description}
              </div>
            )}
            {zoneLabels.length > 0 && (
              <div className="empowerment-badge-list">
                {zoneLabels.map((label, badgeIndex) => {
                  const badgeCategory = getEmpowermentZoneCategory(label);
                  const baseClass =
                    badgeCategory === EMPOWERMENT_ZONE_TYPES.EMPOWERMENT
                      ? "empowerment-badge empowerment"
                      : badgeCategory === EMPOWERMENT_ZONE_TYPES.RURAL
                      ? "empowerment-badge rural"
                      : "empowerment-badge";
                  const dualClass = hasMultipleZones
                    ? "dual-empowerment"
                    : "";
                  return (
                    <div
                      key={`${label}-${badgeIndex}`}
                      className={`${baseClass} ${dualClass}`.trim()}
                    >
                      {label}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Meta info (codes and counts) */}
            <div className="result-card-meta">
              <span>
                NAICS: <b>{naicsDisplay}</b>
              </span>
              <span>
                Census Tract: <b>{censusTractDisplay}</b>
              </span>
            </div>
            <div className="result-card-meta secondary-meta">
              <span className="employees-block metric-inline">
                <img
                  src={Person}
                  width="18"
                  height="18"
                  alt="Employees"
                />
                <span className="metric-label">Emp.</span>
                <b>{employeesDisplay}</b>
              </span>
              <span className="annual-hires-block metric-inline">
                <img
                  src={AnnualHires}
                  width="18"
                  height="18"
                  alt="Annual hires"
                />
                <span className="annual-hires-label">Annual hires:</span>
                {renderAnnualHiresField()}
              </span>
            </div>
            <div className="partition-line"></div>
            <div className="result-card-credits">
              <div>
                <span className="credit-label">
                  Projected Credit:
                </span>
                <br />
                <span className="credit-value">
                  {showProjectedLoading
                    ? "Loading..."
                    : resolvedProjectedCredit !== null
                    ? `$${Number(resolvedProjectedCredit).toLocaleString(undefined, {
                        maximumFractionDigits: 2,
                      })}`
                    : "No Data"}
                </span>
              </div>
              <div style={{ marginTop: 8 }}>
                <span className="credit-label eligible-credit-label">
                  Eligible Credits:
                  {!showEligibleLoading && (
                    <span className="eligible-credit-count">
                      {totalEligibleCredits}
                    </span>
                  )}
                </span>
                <br />
                <div className="result-eligible-wrapper">
                  {showEligibleLoading ? (
                    <span style={{ color: "#aaa" }}>Loading...</span>
                  ) : totalEligibleCredits > 0 ? (
                    <div className="result-eligible-chips">
                      <div className="result-eligible-chip-row">
                        {visibleEligibleCredits.map((c, i) => (
                          <span
                            className="credit-chip"
                            key={i}
                            data-fullcredit={c}
                          >
                            {truncateCredit(c)}
                          </span>
                        ))}
                      </div>
                      {hasMoreEligibleCredits && (
                        <div className="result-eligible-hover">
                          <button
                            type="button"
                            className="result-eligible-hover-button"
                            aria-label={`Show all ${totalEligibleCredits} eligible credits`}
                            title={`View all ${totalEligibleCredits} eligible credits`}
                            onClick={(event) => event.stopPropagation()}
                          >
                            +{extraEligibleCredits}
                          </button>
                          <div className="result-eligible-hover-panel">
                            <div className="result-eligible-hover-title">
                              Eligible Credits ({totalEligibleCredits})
                            </div>
                            <div className="result-eligible-hover-list">
                              {creditNamesForScope.map((credit, hoverIndex) => (
                                <span key={`${businessId || index}-credit-full-${hoverIndex}`}>
                                  {credit}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <span style={{ color: "#aaa" }}>No Data</span>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="result-card-footer">
              <Button
                label={
                  <span style={{ color: "#000" }}>
                    View Tax Credit Analysis{" "}
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 22 22"
                      fill="none"
                      style={{ verticalAlign: "middle", marginLeft: 5, marginBottom: 2 }}
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.75 11H14.25" stroke="#000" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M11 7.75L14.25 11L11 14.25" stroke="#000" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                }
                className="view-btn"
                variant="outlined"
                onClick={() => {
                  const preparedBusiness = buildBusinessForNavigation(post);
                  dispatch(updateSelectedCompnies([preparedBusiness]));
                  dispatch(updateCompanies([preparedBusiness]));
                  if (currentRole === "Salesperson") {
                    navigate("/salesperson/taxCreditAnalysis", {
                      state: { selectedList: [preparedBusiness], companies: [preparedBusiness] },
                    });
                  } else if (currentRole === "Manager") {
                    navigate("/manager/taxCreditAnalysis", {
                      state: { selectedList: [preparedBusiness], companies: [preparedBusiness] },
                    });
                  } else if (currentRole === "CompanyAdmin") {
                    navigate("/companyadmin/taxCreditAnalysis", {
                      state: { selectedList: [preparedBusiness], companies: [preparedBusiness] },
                    });
                  }
                }}
              />
          </div>
        </div>
      </div>
    );
  };

  const renderResultsView = () => {
    const { exact: allExact, additional: allAdditional } = splitExactAdditionalFromList(
      allBusinesses,
      filters?.businessName,
      filters
    );
    const { exact: exactDisplayed, additional: additionalDisplayed } = splitExactAdditionalFromList(
      mapBusinessesToShow,
      filters?.businessName,
      filters
    );
    const hasExactOnPage = exactDisplayed.length > 0;
    const hasAdditionalOnPage = additionalDisplayed.length > 0;
    const hasGlobalExact = allExact.length > 0;
    const totalMatches = effectiveResultsCount;
    const hasBaseResults = allBusinesses.length > 0;
    const noVisibleBusinesses = !visibleBusinesses.length && hasBaseResults;

    return (
      <div className="blogs-creditmap-wrapper">
        <div className="blogs-view-section">
          <div className="blogs-view-toggle-row">
            <div style={{ position: "relative" }} ref={downloadMenuRef}>
              <button
                type="button"
                className={`blogs-download-view-button${filtersLocked ? " disabled" : ""}`}
                disabled={filtersLocked}
                onClick={() => setDownloadMenuOpen((v) => !v)}
              >
                <img src={NavDownloadArrow} alt="" width="14" height="14" /> Download ▾
              </button>
              {downloadMenuOpen && !filtersLocked && (
                <div style={{ position: "absolute", left: 0, top: "calc(100% + 4px)", zIndex: 999, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.12)", minWidth: 170, overflow: "hidden" }}>
                  <button
                    type="button"
                    style={{ display: "block", width: "100%", padding: "10px 16px", textAlign: "left", background: "none", border: "none", borderBottom: "1px solid #f3f4f6", cursor: "pointer", fontSize: 13, color: "#374151" }}
                    onClick={() => { setDownloadMenuOpen(false); (viewMode === "map" ? handleDownloadMapView : handleDownloadGraphView)(); }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#f9fafb")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
                  >
                    Download as HTML
                  </button>
                  <button
                    type="button"
                    style={{ display: "block", width: "100%", padding: "10px 16px", textAlign: "left", background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#374151" }}
                    onClick={() => { setDownloadMenuOpen(false); (viewMode === "map" ? handleDownloadMapViewAsPdf : handleDownloadGraphViewAsPdf)(); }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#f9fafb")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
                  >
                    Download as PDF
                  </button>
                </div>
              )}
            </div>
            <div className="blogs-view-toggle-group">
              <span className="blogs-view-toggle-label">View:</span>
              <div className="blogs-view-toggle-buttons">
                <button
                  type="button"
                  className={`blogs-view-toggle-button${
                    viewMode === "map" ? " active" : ""
                  }`}
                  onClick={() => handleViewModeChange("map")}
                  aria-pressed={viewMode === "map"}
                >
                  Map View
                </button>
                <button
                  type="button"
                  className={`blogs-view-toggle-button${
                    viewMode === "graph" ? " active" : ""
                  }`}
                  onClick={() => handleViewModeChange("graph")}
                  aria-pressed={viewMode === "graph"}
                >
                  Graph View
                </button>
              </div>
            </div>
          </div>
          <div className="blogs-view-surface">
            {viewMode === "map" ? (
              <MapView
                businesses={mapBusinessesToShow}
                taxCreditData={taxCreditData}
                taxCreditLoading={taxCreditLoading}
                initialCenter={searchMapCenter}
                onMarkerSelect={toggleBusinessSelection}
                selectedBusinessIds={selectedBusinessIds}
              />
            ) : (
              renderGraphView()
            )}
          </div>
        </div>

        <div className="blogs-results-section">
          <div className="blogs-results-header">
            <div className="blogs-results-header-left">
              <div className="blogs-results-title-row">
                <div className="blogs-creditmap-results-title">
                  All Tax Credit Details
                  {typeof totalMatches === "number" && (
                    <span className="blogs-creditmap-results-count">
                      {" "}
                      ({totalMatches})
                    </span>
                  )}
                </div>
                <div
                  className={`blogs-filter-dropdown${
                    isFilterMenuOpen ? " open" : ""
                  }${filtersLocked ? " disabled" : ""}`}
                  ref={filterMenuRef}
                >
                  <button
                    type="button"
                    className="blogs-filter-toggle"
                    onClick={handleFilterToggleClick}
                    disabled={filtersLocked}
                    aria-haspopup="true"
                    aria-expanded={isFilterMenuOpen}
                  >
                    Filter Results
                    <span className="blogs-filter-caret" />
                  </button>
                  {isFilterMenuOpen && (
                    <div className="blogs-filter-menu">
                      <div className="blogs-filter-section">
                        <div className="blogs-filter-section-title">
                          Empowerment Zone
                        </div>
                        <div className="blogs-filter-options">
                          {empowermentFilterOptions.map((option) => (
                            <label
                              key={option.value}
                              className="blogs-filter-option"
                            >
                              <input
                                type="radio"
                                name="empowerment-filter"
                                value={option.value}
                                checked={empowermentFilter === option.value}
                                onChange={() =>
                                  setEmpowermentFilter(option.value)
                                }
                              />
                              <span>{option.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="blogs-filter-section">
                        <div className="blogs-filter-section-title">
                          NAICS Filter
                        </div>
                        <input
                          type="text"
                          className="blogs-filter-input"
                          value={naicsFilter}
                          onChange={(e) => handleNaicsFilterChange(e.target.value)}
                          placeholder="Enter 2-6 digit NAICS"
                          maxLength={6}
                        />
                        <div
                          className={`blogs-filter-helper${
                            isNaicsFilterPending ? " warning" : ""
                          }`}
                        >
                          {isNaicsFilterPending
                            ? "Enter at least 2 digits to apply this filter."
                            : isNaicsFilterActive
                            ? `Filtering NAICS codes matching ${sanitizedNaicsFilter}.`
                            : "Filter results by a 2-6 digit NAICS code."}
                        </div>
                      </div>
                      <div className="blogs-filter-section">
                        <div className="blogs-filter-section-title">
                          Categories
                        </div>
                        {availableCreditCategories.length ? (
                          <div className="blogs-filter-options scrollable">
                            {availableCreditCategories.map((category) => (
                              <label
                                key={category.key}
                                className="blogs-filter-option"
                              >
                              <input
                                type="checkbox"
                                checked={Array.isArray(creditCategoryFilter) && creditCategoryFilter.includes(category.key)}
                                onChange={() =>
                                  handleCategoryFilterToggle(category.key)
                                }
                              />
                              <span>
                                {category.label}
                              </span>
                            </label>
                          ))}
                        </div>
                        ) : (
                          <div className="blogs-filter-helper">
                            Categories will appear after credits are loaded.
                          </div>
                        )}
                      </div>
                      <div className="blogs-filter-section">
                        <div className="blogs-filter-section-title">
                          Tax Credit Type
                        </div>
                        <div className="blogs-filter-options">
                          {taxCreditFilterOptions.map((option) => (
                            <label
                              key={option.value}
                              className="blogs-filter-option"
                            >
                              <input
                                type="radio"
                                name="tax-credit-filter"
                                value={option.value}
                                checked={taxCreditFilter === option.value}
                                onChange={() =>
                                  setTaxCreditFilter(option.value)
                                }
                              />
                              <span>{option.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="blogs-filter-footer">
                        <button
                          type="button"
                          className="blogs-filter-clear"
                          onClick={handleResetFilters}
                        >
                          Clear Filters
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {filtersLocked && (
                <div className="blogs-filter-status">
                  Filters unlock when all cards finish loading.
                </div>
              )}
              {visibleBusinesses.length > 0 && (
                <div className="blogs-creditmap-total-row">
                  {showFederalWotcChip && (
                    <div className="blogs-creditmap-total-chip">
                      Federal WOTC:{" "}
                      <span>
                        {formatCreditTotal(creditTotals.federalWotc || 0)}
                      </span>
                    </div>
                  )}
                  {showFederalNonWotcChip && (
                    <div className="blogs-creditmap-total-chip">
                      Federal (Non-WOTC):{" "}
                      <span>
                        {formatCreditTotal(creditTotals.federalNonWotc || 0)}
                      </span>
                    </div>
                  )}
                  {showStateTotalChip && (
                    <div className="blogs-creditmap-total-chip">
                      State:{" "}
                      <span>{formatCreditTotal(creditTotals.state || 0)}</span>
                    </div>
                  )}
                  {showGrandTotalChip && (
                    <div className="blogs-creditmap-total-chip total">
                      Total:{" "}
                      <span>{formatCreditTotal(creditTotals.total)}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          <div className="blogs-creditmap-results-actions">
            <Button
              label={allVisibleSelected ? "Unselect Visible" : "Select Visible"}
              variant="outlined"
              onClick={handleToggleVisibleSelection}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                borderColor: "#D5D8DC",
                color: "#23244c",
                minWidth: 130,
              }}
            />
          </div>
          </div>
          <hr />
          {shouldShowNoRecordsMessage ? (
            <div className="blogs-creditmap-empty">
              <div className="blogs-creditmap-empty-title">No records found</div>
              <div className="blogs-creditmap-empty-text">
                We couldn't find matching businesses. You can calculate tax credits
                manually by entering business details.
              </div>
              {renderManualPromptButton()}
            </div>
          ) : (
            <div className="blogs-creditmap-results-body">
              {noVisibleBusinesses && (
                <div className="blogs-creditmap-empty">
                  <div className="blogs-creditmap-empty-title">
                    {hasUserAppliedFilters
                      ? "No businesses match the selected filters"
                      : "No businesses to display"}
                  </div>
                  <div className="blogs-creditmap-empty-text">
                    Try adjusting the filters or load more results to view
                    additional businesses.
                  </div>
                </div>
              )}
              {isLoading && !allBusinesses.length && !isLoadingMore && (
                <div className="w-100 text-center pt-3">
                  <CircularProgress
                  size={28}
                  thickness={5}
                  sx={{ color: "#23244c" }}
                  />
                  </div>
                )}
              <div className="blogs-card-grid-wrapper">
                {hasExactOnPage && (
                  <div className="blogs-creditmap-section">
                    <div className="partition-header">
                      Exact Matches ({exactDisplayed.length})
                    </div>
                    <div className="blogs-creditmap-card-grid">
                      {exactDisplayed.map((c, i) => renderCard(c, `exact-${i}`))}
                    </div>
                  </div>
                )}
                {!hasGlobalExact && allAdditional.length > 0 && (
                  <div className="blogs-creditmap-section">
                    <div className="partition-header">Exact Matches (0)</div>
                    <div className="blogs-creditmap-empty-text" style={{ marginBottom: 12 }}>
                      None of the businesses were an exact match for your search terms.
                    </div>
                    {renderManualPromptButton()}
                  </div>
                )}
                {hasAdditionalOnPage && (
                  <div className="blogs-creditmap-section">
                    <div className="partition-header">
                      Additional Results ({additionalDisplayed.length})
                    </div>
                    <div className="blogs-creditmap-card-grid">
                      {additionalDisplayed.map((c, i) => renderCard(c, `add-${i}`))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          {!shouldShowNoRecordsMessage && (
            <div className="blogs-pagination">
              {hasMoreResults && isLoadingMore ? (
                <div className="blogs-auto-load-indicator">
                  <CircularProgress size={20} thickness={5} sx={{ color: "#3b3f52" }} />
                  <span>Loading more results...</span>
                </div>
                ) : (
                  !isLoading && !isLoadingMore && (
                  <div className="blogs-creditmap-end">No more results</div>
                )
              )}
            </div>
          )}
      </div>
      </div>
    );
  };

  return (
    <>
      <Header />
      <div className={isMobile ? "p-1" : "p-4 mx-4"}>
        <div className="">
          <div className={`d-flex ${isMobile ? "px-3" : ""}`}>
            <div className="p-right">
              <img
                src={backNav}
                height={isMobile ? "24px" : "36px"}
                width={isMobile ? "24px" : "36px"}
                onClick={() => backNavigation()}
                alt="Back Nav"
              ></img>
            </div>
            <div>
              <h2
                className={`blogs-title ${!isMobile ? "font-32" : " font-20"}`}
              >
                Review & Update Information
              </h2>
            </div>
          </div>
        </div>
        <div
          className={`locationDetails blogs-layout ${
            !isMobile ? " p-4 blogs-height" : "py-2"
          }`}
          style={{
            paddingBottom: selectedBusinesses.length > 0 ? 140 : undefined,
          }}
        >
          {error && (
            <div
              className={`errorImageCredit ${
                isMobile ? " px-1 mx-1" : " px-2 mx-3"
              }`}
            >
              <img
                src="/DismissCircle.png"
                alt=""
                height="16px"
                width="16px"
                className="my-auto"
              ></img>
              <span className="my-auto">{error}</span>
            </div>
          )}
          {renderResultsView()}
        </div>
      </div>
      {selectedBusinesses.length > 0 && !isSelectionTrayCollapsed && (
        <div className={`blogs-selected-footer ${isMobile ? "mobile" : ""}`}>
          <div className="blogs-selected-chips">
            {selectedBusinesses.map((item) => (
              <Chip
                key={getBusinessSelectionKey(item)}
                label={stripHtml(item.businessName || "Business")}
                deleteIcon={<CloseIcon />}
                onDelete={() => removeSelectedBusiness(item)}
                sx={{ borderRadius: "8px" }}
              />
            ))}
          </div>
          <div className="blogs-selected-actions">
            <LoadingButton
              variant="contained"
              size="medium"
              onClick={downloadSelectedAsExcel}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                backgroundColor: "#FFCC33",
                color: "#000",
                borderColor: "#FFCC33",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#e6b800",
                  borderColor: "#e6b800",
                  boxShadow: "none",
                },
              }}
            >
              {!isMobile && (
                <img
                  src={CSV}
                  width="20"
                  height="20"
                  alt="download excel"
                  style={{ marginRight: 8 }}
                />
              )}
              {isMobile ? "Excel" : "Download Excel"}
            </LoadingButton>
            <LoadingButton
              variant="contained"
              size="medium"
              onClick={downloadSelectedAsPdf}
              loading={isPdfDownloading}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                backgroundColor: "#FFCC33",
                color: "#000",
                borderColor: "#FFCC33",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#e6b800",
                  borderColor: "#e6b800",
                  boxShadow: "none",
                },
              }}
            >
              {!isMobile && (
                <img
                  src={PDFIcon}
                  width="20"
                  height="20"
                  alt="download pdf"
                  style={{ marginRight: 8 }}
                />
              )}
              {isMobile ? "PDF" : "Download PDF"}
            </LoadingButton>
            <button
              type="button"
              className="blogs-selected-close"
              onClick={() => setSelectionTrayCollapsed(true)}
            >
              <CloseIcon fontSize="small" />
              <span>Hide Selected</span>
            </button>
          </div>
        </div>
      )}
      {selectedBusinesses.length > 0 && isSelectionTrayCollapsed && (
        <button
          type="button"
          className={`blogs-selected-toggle ${isMobile ? "mobile" : ""}`}
          onClick={() => setSelectionTrayCollapsed(false)}
        >
          Show selected ({selectedBusinesses.length})
        </button>
      )}
      {selectedBusinesses.length > 0 && (
        <div
          style={{ position: "absolute", left: "-9999px", top: 0, backgroundColor: "#fff" }}
        >
          <div
            id="blogs-selected-pdf-content"
            style={{ borderRadius: 0, background: "white", paddingBottom: 64 }}
          >
            {selectedBusinesses.map((business, index) => {
              const prepared = buildBusinessForNavigation(business);
              const resolvedCredits =
                resolveTaxCreditsForBusiness(prepared) || [];
              const projectedCredit =
                resolvedCredits?.[0]?.totalProjectedTaxCreditAmount ??
                taxCreditData[prepared.prospectBusinessId]?.projectedCredit ??
                null;
              const projectedCreditFormatted =
                typeof projectedCredit === "number"
                  ? Number(projectedCredit).toLocaleString(undefined, {
                      style: "currency",
                      currency: "USD",
                      maximumFractionDigits: 2,
                    })
                  : "No Data";
              const annualSalesFormatted = prepared.salesVolume
                ? Number(prepared.salesVolume).toLocaleString(undefined, {
                    style: "currency",
                    currency: "USD",
                    maximumFractionDigits: 2,
                  })
                : "No Data";
              const businessPhone =
                prepared.phone || "No Data Available";
              const businessEmail =
                prepared.email || "No Data Available";
              const hiresValue = resolveAnnualHiresValue(prepared);
              const hiresDisplay =
                typeof hiresValue === "number" ? hiresValue : hiresValue || "No Data";
              const creditDescriptions =
                getTaxCreditDescriptions(resolvedCredits);
              const employeesDisplay = formatEmployeesDisplay(prepared);

              return (
                <div
                  key={getBusinessSelectionKey(prepared)}
                  style={{
                    pageBreakBefore: index === 0 ? "auto" : "always",
                    background: "white",
                  }}
                >
                  <div
                    style={{
                      background: "#444549",
                      color: "#fff",
                      borderRadius: "0 16px 0 0",
                      padding: "22px 32px 18px 32px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      WebkitPrintColorAdjust: "exact",
                      printColorAdjust: "exact",
                      colorAdjust: "exact",
                    }}
                  >
                    <div>
                      <div
                        style={{ fontSize: 23, fontWeight: 600, letterSpacing: "-1px" }}
                      >
                        Tax Credit Analysis
                      </div>
                      <div style={{ fontSize: 14, marginTop: 8 }}>
                        Provided by: {appUserName}
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        justifyContent: "flex-end",
                        minWidth: 120,
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 700,
                          fontSize: 15,
                          letterSpacing: 1,
                          color: "#fff",
                          marginBottom: 2,
                          textTransform: "uppercase",
                        }}
                      >
                        POWERED BY:
                      </div>
                      <img
                        src={canaryLogo}
                        alt="canary"
                        style={{ height: 28, width: "auto", marginBottom: 4 }}
                      />
                      <div style={{ fontSize: 14, color: "#fff" }}>
                        {dayjs().format("M/D/YYYY")}
                      </div>
                    </div>
                  </div>
                  <div style={{ padding: "2.5rem 2.5rem 1.5rem 2.5rem" }}>
                    <div
                      style={{ color: "#888", fontSize: 14, marginBottom: 10 }}
                    >
                      Business Profile
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "180px 1fr",
                        rowGap: 8,
                        columnGap: 16,
                        fontSize: 16,
                      }}
                    >
                      <div>Name</div>
                      <div style={{ fontWeight: 600 }}>
                        {stripHtml(prepared.businessName || "No Data")}
                      </div>
                      <div>Category</div>
                      <div>{prepared.siC01Description || "No Data"}</div>
                      <div>Location</div>
                      <div>{formatBusinessAddress(prepared)}</div>
                      <div>Employees</div>
                      <div>{employeesDisplay}</div>
                      <div>Annual hires</div>
                      <div>{hiresDisplay}</div>
                      <div>Phone</div>
                      <div>{businessPhone}</div>
                      <div>Email</div>
                      <div>{businessEmail}</div>
                    </div>
                    <hr
                      style={{
                        border: "none",
                        borderTop: "1px solid #eee",
                        margin: "30px 0 18px 0",
                      }}
                    />
                    <div
                      style={{ color: "#888", fontSize: 14, marginBottom: 10 }}
                    >
                      Tax Credit Analysis
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "180px 1fr",
                        rowGap: 10,
                        columnGap: 16,
                        fontSize: 16,
                      }}
                    >
                      <div style={{ fontSize: 15 }}>Projected Credit</div>
                      <div style={{ fontWeight: 700, fontSize: 18 }}>
                        {projectedCreditFormatted}
                      </div>
                      <div>Annual Sales</div>
                      <div>{annualSalesFormatted}</div>
                      <div style={{ alignSelf: "start" }}>Credits Identified</div>
                      <div>
                        {creditDescriptions.length > 0 ? (
                          creditDescriptions.map((desc, i) => (
                            <div key={i} style={{ marginBottom: 10 }}>
                              <span style={{ fontWeight: 600 }}>
                                {desc.split(":")[0]}
                              </span>
                              <div
                                style={{
                                  fontSize: 15,
                                  color: "#333",
                                  marginLeft: 2,
                                }}
                              >
                                {desc.split(":").slice(1).join(":").trim()}
                              </div>
                            </div>
                          ))
                        ) : (
                          <span style={{ color: "#aaa" }}>No Data</span>
                        )}
                      </div>
                    </div>
                    <div
                      className="pdf-keep-together"
                      style={{
                        marginTop: 28,
                        fontSize: 13,
                        color: "#777",
                        breakInside: "avoid",
                        pageBreakInside: "avoid",
                        pageBreakBefore: "avoid",
                        pageBreakAfter: "avoid",
                        position: "relative",
                        zIndex: 2,
                        background: "#fff",
                      }}
                    >
                      <b>Disclaimer:</b> Projected savings are based on estimates
                      related to employee hiring and retention, reflecting
                      adjustments to annual hire data. Actual results may vary.
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};

export default Blog;
